import Vue from 'vue';
import PQueue from 'p-queue';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { VxlBatchDownloader } from '@/plugins/vxlBatchDownloader';
import { offlineStorage } from '@/services/offline/offlineStorage';
import { computeOfflineSize, safeLoId, STORAGE_DIR, OFFLINE_DIR } from '@/services/offline/offlinePaths';
import { imageCache } from '@/services/offline/imageCache';
import { hlsNativeDownloader, InsufficientDiskSpaceError, ExternalProviderError, CancelledError } from '@/services/download/hlsNativeDownloader';
import { pdfNativeDownloader } from '@/services/download/pdfNativeDownloader';
import { audioNativeDownloader } from '@/services/download/audioNativeDownloader';
import { downloadNotification } from '@/services/downloadNotification';
import { fetchSubtitleList, downloadSubtitleFiles } from '@/services/offline/subtitleService';
import {
  findLO,
  collectImageUrls,
  collectClassesFromGrid,
  hasAnyDownloads,
  hasWebcastCredentials,
  isNetworkError,
  shouldSkipDownload,
  flattenGridForDownloads
} from '@/services/download/downloadHelpers';
import {
  showInsufficientSpace,
  showMissingCredentials,
  showMigrationInProgress,
  showNetworkPaused,
  showRetryExhausted,
  showCellularBlocked,
  showExternalProvider
} from '@/services/download/downloadToasts';

// Schema de cifragem do cache offline. Bumpar quando o formato no disco mudar
// (algoritmo, IV scheme, layout de arquivos). Bumpar invalida TODA a base
// instalada — qualquer LO baixado em versão anterior tem que ser re-baixado.
//
// v0 → pre-cripto (segmentos .ts plaintext no disco)
// v1 → AES-128-CBC + IV=sequence_number; File Key per-LO no KeyVault hardware
// v2 → bump do POC de MP4 progressivo cifrado (caminho removido; hoje só HLS).
//      Mantido em 2 mesmo sem MP4: voltar pra 1 faria loadState ver "versão
//      maior que a atual" e invalidaria o cache HLS de toda a base instalada.
const CURRENT_ENCRYPTION_VERSION = 2;

// Auto-resume só dispara em wifi por default. User economiza dados móveis sem
// precisar pensar. Click manual no ícone âmbar sempre funciona (intencional).
// Quando uma toggle UI for adicionada, esse default fica configurável.
const DEFAULT_DOWNLOAD_ON_CELLULAR = false;

// Painel de diagnóstico da fila de sync offline (OfflineSyncDebug). Default off:
// só aparece quando ligado em Configurações de download. Persistida.
const DEFAULT_SHOW_OFFLINE_SYNC_PANEL = false;

// Prioridade de reprodução: com aula em streaming aberta, batches novos entram
// com 1 conexão em vez de 6, pra não disputar banda com o player. Default on —
// quem quer download a todo custo desliga em Configurações de download.
// Só o Android honra a concorrência por batch hoje; no iOS o parâmetro é
// aceito e ignorado pelo plugin (gate nativo é a fase 2 da feature).
const DEFAULT_PLAYBACK_PRIORITY = true;
// Concorrência usada quando a prioridade de reprodução está ativa e há
// streaming em andamento no instante do enqueue.
const PLAYBACK_PRIORITY_CONCURRENCY = 1;

// Limite de tentativas automáticas de resume por LO. Após N falhas
// consecutivas de rede, o partial vira "frozen": ícone continua âmbar pulsando
// mas auto-resume ignora. User precisa clicar manual (que reseta o contador).
// Protege contra loop perpétuo em rede flaky (5G ruim, etc).
const MAX_AUTO_RETRY = 5;
// Flag module-level setado em loadState quando o storage tem versão antiga.
// Consumido pela action migrateLegacyDownloads (chamada do main.js no boot)
// pra purgar fisicamente o diretório offline/. Sem isso, arquivos plaintext
// ficariam órfãos no disco e o reconcile do plugin os marcaria como done.
let _legacyMigrationNeeded = false;

// Quando o disco enche no meio da fila, todas as próximas aulas vão falhar
// pelo mesmo motivo — drenar a fila e resetar status de TUDO que estava
// pendente evita: (a) toast repetido a cada item da fila, (b) UI mostrando
// "baixando" sem progresso pra LOs que nunca vão completar.
// Importante: só items ainda-não-iniciados são cancelados (p-queue.clear()
// não interrompe o currently-running). O catch que chamou isso já é
// responsável por marcar a LO atual como idle. Mora aqui (não em helpers)
// porque mexe em downloadQueue e downloadNotification — state local do módulo.
function _drainDownloadsOnDiskFull (commit, state) {
  downloadQueue.clear();
  Object.entries(state.downloads).forEach(([enrollId, types]) => {
    Object.entries(types.learningObjects || {}).forEach(([loId, status]) => {
      if (status === 'downloading' || status === 'queued') {
        commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'idle' });
        commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress: 0 });
      }
    });
    Object.entries(types.classes || {}).forEach(([classId, status]) => {
      if (status === 'downloading' || status === 'queued') {
        commit('SET_STATUS', { enrollId, type: 'classes', id: classId, status: 'idle' });
        commit('SET_PROGRESS', { enrollId, type: 'classes', id: classId, progress: 0 });
      }
    });
  });
  downloadNotification.cancel();
}

const downloadQueue = new PQueue({ concurrency: 1 });

// Sessão de fila no nativo (Android). O FGS de download é por-batch (= por
// aula) e caía 5s depois de cada batch; o gap entre aulas é orquestrado AQUI,
// no WebView — e com o app em background o processo vira "cached" no instante
// em que o FGS cai e o OS congela tudo (One UI faz isso em segundos com
// economia de bateria). A fila morria sem erro na primeira troca de aula.
// beginSession avisa o service pra segurar o foreground até o endSession do
// 'idle'. iOS é stub no-op (URLSession.background não precisa).
let _nativeSessionActive = false;
function _beginNativeSession () {
  if (!Capacitor.isNativePlatform() || _nativeSessionActive) return;
  _nativeSessionActive = true;
  VxlBatchDownloader.beginSession().catch(() => { _nativeSessionActive = false; });
}
function _endNativeSession () {
  if (!_nativeSessionActive) return;
  _nativeSessionActive = false;
  VxlBatchDownloader.endSession().catch(() => {});
}

// Ledger de AULAS do ciclo atual da fila, para o "X de Y" da notificação.
//
// Precisa ser em aulas, e não derivado da PQueue, porque uma turma ocupa UMA task
// representando N aulas: `downloadQueue.size` não corresponde a nada que o usuário
// veja. Antes havia duas contabilidades desconexas — o caminho individual usava um
// contador global que o downloadClass nunca incrementava, e o caminho de turma
// reportava só o `pendingTotal` da própria turma. Com as duas na fila ao mesmo
// tempo, o total exibido era o de quem estava baixando naquele instante e ignorava
// o resto: enfileirar 6 aulas durante "1 de 3" não mudava nada.
//
// `total` cresce no clique (antes de enfileirar) e `started` avança quando cada
// aula de fato começa a transferir — então a notificação diz "baixando a aula K
// de N", que é o que o usuário quer saber. Ambos zeram no idle da fila.
let _notifTotal = 0;
let _notifStarted = 0;

// Registra N aulas novas e republica a notificação IMEDIATAMENTE. O refresh é o
// ponto central: sem ele o total só apareceria no próximo tick de progresso da
// aula em curso — que numa aula longa leva minutos.
function _notifAdd (n) {
  if (!(n > 0)) return;
  _notifTotal += n;
  downloadNotification.refreshTotals(Math.max(1, _notifStarted), _notifTotal);
}

// Corrige o total já registrado (delta pode ser negativo). Usado quando a turma
// descobre, ao rodar, quantas aulas vai processar de fato, e quando um early-return
// devolve as aulas que havia reservado no clique.
function _notifAdjust (delta) {
  if (!delta) return;
  _notifTotal = Math.max(_notifStarted, _notifTotal + delta);
  downloadNotification.refreshTotals(Math.max(1, _notifStarted), _notifTotal);
}

function _notifStart () {
  _notifStarted++;
}

// Decisão do user de "baixar mesmo em cellular" válida apenas pelo ciclo atual
// da fila — quando a fila zera, reseta pra forçar nova confirmação no próximo
// clique. Ortogonal a state.downloadOnCellular (que é persistente). Não-reativo;
// só é consultado no instante do clique pelo gate em ComposedHeader.
let _cellularConfirmedThisCycle = false;

// LOs cancelados pelo usuário (remoção) enquanto estavam na fila ou baixando.
// Chave `${enrollId}:${loId}`.
//
// Por que precisa existir: remover um curso/turma cancela o batch EM VOO, mas as
// tasks já enfileiradas na PQueue continuam lá, e o laço do downloadClass tem um
// snapshot congelado dos pendentes. Sem esta lista, remover um curso no meio do
// download fazia as aulas seguintes continuarem baixando e REAPARECEREM — e, no
// caminho de purga por DRM, o conteúdo que o produtor proibiu voltava sozinho.
// p-queue v6 não permite remover tasks já agendadas; marcar e checar na execução
// é o caminho viável.
const _cancelledDownloads = new Set();
const _cancelKey = (enrollId, loId) => `${enrollId}:${loId}`;
// Chave de escopo de TURMA. Distingue "cancelei esta aula" (pula a aula e segue
// o lote) de "cancelei a turma/curso" (aborta o lote inteiro). Sem essa
// distinção, cancelar uma aula no meio de uma turma matava o download das
// demais e as deixava presas em 'queued'.
const _classCancelKey = (enrollId, classId) => `${enrollId}:class:${classId}`;
// Chave de escopo de CURSO. As de turma só alcançam turmas que já existem em
// `downloads[enrollId].classes` — ou seja, que já commitaram status. Num
// "baixar curso" o laço despacha turma a turma, cada uma com preflight de rede,
// então quase todas ainda não existem quando o usuário cancela: elas passavam
// batido e continuavam baixando depois do curso ter sido removido.
const _enrollCancelKey = (enrollId) => `${enrollId}:enroll`;

// Pré-buscar a IA de um curso onde o produtor a desligou é request jogada fora,
// e é justamente onde o endpoint tende a não ter nada. Mesmo critério que a aba
// usa offline, onde o gate degrada para a flag do enroll.
//
// Só pula quando o `false` é EXPLÍCITO: no boot e na reconexão o
// resumePausedDownloads retoma downloads com `enroll.current` ainda null ou
// apontando pra outro curso, e gatear por verdade calaria o cache de IA de toda
// retomada automática, em silêncio.
const aiPrefetchAllowed = (enroll) =>
  !(enroll && enroll.settings && enroll.settings.enableIA === false);

downloadQueue.on('idle', () => {
  _notifTotal = 0;
  _notifStarted = 0;
  _cellularConfirmedThisCycle = false;
  // Fila vazia: nada mais a cancelar. Sem isto, um download futuro do mesmo LO
  // seria abortado por uma remoção antiga.
  _cancelledDownloads.clear();
  // Fila esvaziou de verdade (último download já resolveu): libera o FGS
  // Android segurar o foreground. Ele ainda espera a carência curta antes de
  // fechar a notificação — um clique logo em seguida reaproveita o service.
  _endNativeSession();
});

const STORAGE_KEY = 'ead_downloads';

// Schema v0 de positions: { [enrollId]: { learningObjects: { [loId]: seconds } } }
// Schema v1 (atual):     { [enrollId]: { learningObjects: { [loId]: { seconds, updatedAt } } } }
// Migração transparente: campos antigos (number) viram { seconds, updatedAt: 0 }.
// updatedAt=0 é semanticamente "desconhecido" e serve pra ordenação estável no
// "Continue assistindo" sem fingir um timestamp falso.
function _normalizePositions (rawPositions) {
  const out = {};
  Object.entries(rawPositions || {}).forEach(([enrollId, types]) => {
    const learningObjects = {};
    Object.entries((types && types.learningObjects) || {}).forEach(([loId, value]) => {
      if (value && typeof value === 'object' && typeof value.seconds === 'number') {
        learningObjects[loId] = { seconds: value.seconds, updatedAt: value.updatedAt || 0 };
      } else if (typeof value === 'number') {
        learningObjects[loId] = { seconds: value, updatedAt: 0 };
      }
    });
    out[enrollId] = { learningObjects };
  });
  return out;
}

// Lista canônica dos campos persistidos em localStorage. Adicionar campo novo
// aqui + colocar no shape devolvido por loadState. saveState serializa o
// state inteiro mas filtra por essa lista pra não vazar internal-only props
// (ex: migrating, pendingDrmPurge — controle de runtime, não persistência).
// `progress` NÃO entra: o loadState zera todo progresso de item não-concluído no
// boot, então persisti-lo é trabalho jogado fora — e caro. Toda mutation chama
// saveState (JSON.stringify de tudo + setItem síncrono na main thread), e o
// progresso pulsa a cada ~500ms por item em download. Numa turma eram ~4
// serializações/segundo de um payload que passa de 60KB com muitos downloads,
// causando jank contínuo na UI durante todo o download.
const PERSISTED_FIELDS = [
  'downloads', 'cachedEnrolls', 'paths', 'formats', 'positions',
  'partialDownloads', 'downloadOnCellular', 'sizes',
  'downloadQualityWifi', 'downloadQualityLastCellular',
  'showOfflineSyncPanel', 'subtitles', 'playbackPriority'
];

function emptyState () {
  return {
    downloads: {}, progress: {}, cachedEnrolls: {}, paths: {}, formats: {}, positions: {},
    partialDownloads: {}, downloadOnCellular: DEFAULT_DOWNLOAD_ON_CELLULAR, sizes: {},
    subtitles: {},
    showOfflineSyncPanel: DEFAULT_SHOW_OFFLINE_SYNC_PANEL,
    playbackPriority: DEFAULT_PLAYBACK_PRIORITY,
    // null = "primeira vez" (dispara modal de onboarding no 1º download de vídeo).
    // Depois vira 'low' | 'medium' | 'high'.
    downloadQualityWifi: null,
    // Última escolha do user em cellular (pré-seleção no próximo modal cellular).
    // Pode ser null se nunca baixou em cellular.
    downloadQualityLastCellular: null
  };
}

function loadState () {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const storedVersion = parsed.encryptionVersion || 0;

    if (storedVersion < CURRENT_ENCRYPTION_VERSION) {
      // Schema pre-cripto. Os segmentos .ts no disco estão plaintext, mas o
      // manifest local que vamos regerar declara EXT-X-KEY apontando pra
      // vxlkey:// — o VHS tentaria decifrar bytes plaintext = lixo no player.
      // Limpa o estado lógico aqui (status idle, paths vazios) e marca pra
      // purgar fisicamente o diretório offline/ no boot (main.js chama
      // migrateLegacyDownloads). Posições e estrutura cacheada são preservadas
      // — não têm cripto, não há risco.
      console.warn(`[download] cripto schema v${storedVersion} → v${CURRENT_ENCRYPTION_VERSION}: invalidando cache offline existente`);
      _legacyMigrationNeeded = true;
      return {
        ...emptyState(),
        cachedEnrolls: parsed.cachedEnrolls || {},
        positions: _normalizePositions(parsed.positions),
        downloadOnCellular: parsed.downloadOnCellular ?? DEFAULT_DOWNLOAD_ON_CELLULAR,
        downloadQualityWifi: parsed.downloadQualityWifi || null,
        downloadQualityLastCellular: parsed.downloadQualityLastCellular || null,
        showOfflineSyncPanel: parsed.showOfflineSyncPanel ?? DEFAULT_SHOW_OFFLINE_SYNC_PANEL,
        playbackPriority: parsed.playbackPriority ?? DEFAULT_PLAYBACK_PRIORITY
      };
    }

    const downloads = parsed.downloads || {};
    const progress = parsed.progress || {};

    // Sweep de "downloading"/"queued" órfão. Se o app foi morto mid-download
    // (force-close, OOM kill, reboot), o status persistido em localStorage
    // continua 'downloading'/'queued' apesar de não haver mais batch nem fila —
    // ícone fica spinning/relógio forever até o user clicar "remover" ou "baixar"
    // de novo. Demote pra idle e zera progress. partialDownloads preserva os LOs que tinham
    // checkpoint salvo, então a UI continua oferecendo "Continuar" pra eles;
    // os outros vão pro estado inicial e o user re-clica pra começar (o
    // plugin nativo reconcilia segmentos no disco e só baixa o que falta).
    Object.entries(downloads).forEach(([enrollId, enrollState]) => {
      if (!enrollState) return;
      ['learningObjects', 'classes'].forEach(type => {
        Object.entries(enrollState[type] || {}).forEach(([itemId, status]) => {
          if (status === 'downloading' || status === 'queued') {
            enrollState[type][itemId] = 'idle';
            if (progress[enrollId]?.[type]) {
              progress[enrollId][type][itemId] = 0;
            }
          }
        });
      });
    });

    // Self-heal: um LO 'downloaded' nunca deve manter marcador parcial. Sobras
    // (ex: LO que falhou e depois completou via downloadClass, que antes não
    // limpava o partial no sucesso) faziam o resumePausedDownloads re-disparar
    // o download na reconexão e, ao falhar, jogavam o LO pra idle+laranja.
    const partials = parsed.partialDownloads || {};
    Object.entries(partials).forEach(([enrollId, los]) => {
      Object.keys(los || {}).forEach(loId => {
        if (downloads[enrollId]?.learningObjects?.[loId] === 'downloaded') {
          delete los[loId];
        }
      });
    });

    return {
      downloads,
      progress,
      cachedEnrolls: parsed.cachedEnrolls || {},
      paths: parsed.paths || {},
      formats: parsed.formats || {},
      positions: _normalizePositions(parsed.positions),
      partialDownloads: partials,
      downloadOnCellular: parsed.downloadOnCellular ?? DEFAULT_DOWNLOAD_ON_CELLULAR,
      sizes: parsed.sizes || {},
      subtitles: parsed.subtitles || {},
      downloadQualityWifi: parsed.downloadQualityWifi || null,
      downloadQualityLastCellular: parsed.downloadQualityLastCellular || null,
      showOfflineSyncPanel: parsed.showOfflineSyncPanel ?? DEFAULT_SHOW_OFFLINE_SYNC_PANEL,
      playbackPriority: parsed.playbackPriority ?? DEFAULT_PLAYBACK_PRIORITY
    };
  } catch {
    return emptyState();
  }
}

function saveState (state) {
  try {
    const payload = { encryptionVersion: CURRENT_ENCRYPTION_VERSION };
    PERSISTED_FIELDS.forEach(k => { payload[k] = state[k]; });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (e) {
    console.warn('[download] Falha ao salvar estado (storage cheio?):', e);
  }
}

const initial = loadState();

export default {
  namespaced: true,
  state: {
    downloads: initial.downloads,
    // { [enrollId]: { learningObjects: {[id]: status}, classes: {[id]: status} } }
    // status: 'idle' | 'downloading' | 'downloaded'
    progress: initial.progress,
    // { [enrollId]: { learningObjects: {[id]: 0-100}, classes: {[id]: 0-100} } }
    cachedEnrolls: initial.cachedEnrolls,
    // { [enrollId]: true } — indica que a estrutura do curso está cacheada offline
    paths: initial.paths,
    // { [enrollId]: { learningObjects: {[id]: localManifestUrl} } }
    formats: initial.formats,
    // { [enrollId]: { learningObjects: {[id]: 'hls' | 'audio'} } } — formato do
    // arquivo baixado por LO. Vídeo é sempre 'hls': o manifest local é lido
    // direto e o VHS decifra os .ts. Setado junto do SET_PATH.
    positions: initial.positions,
    // { [enrollId]: { learningObjects: {[id]: seconds} } } — posição de reprodução offline
    partialDownloads: initial.partialDownloads,
    // { [enrollId]: { [loId]: { progress, pausedAt, retryCount } } } — LOs que
    // foram pausados por falha de rede (vs erro real) e mantêm arquivos parciais
    // no disco. UI mostra "Continuar" pra eles. Limpa ao concluir ou ao remove().
    // retryCount conta tentativas auto-resume falhadas consecutivas (zera ao
    // clicar manual). Acima de MAX_AUTO_RETRY, auto-resume ignora — só manual.
    downloadOnCellular: initial.downloadOnCellular,
    // Auto-resume só dispara em wifi quando false. Click manual no ícone âmbar
    // ignora essa flag (intenção explícita do user). Default false — economiza
    // dados móveis até virar um toggle de UI explícito.
    migrating: _legacyMigrationNeeded,
    // True enquanto a migração de schema de cifragem está em andamento. Bloqueia
    // novos downloads pra evitar race com rmdir('offline') / nukeAll. Setado
    // false ao fim do action migrateLegacyDownloads. Sem migração necessária,
    // já começa false.
    subtitles: initial.subtitles,
    // { [enrollId]: { learningObjects: { [loId]: [{sigla, nome, filename}] } } }
    // — índice das legendas .vtt baixadas pro dir offline do LO. AUSENTE
    // (undefined) = nunca conseguimos consultar o /info (heal retenta);
    // [] = consultado, aula sem legendas (estado final). Na migração de schema
    // volta a {} junto do emptyState — os arquivos são purgados com o resto.
    sizes: initial.sizes,
    // { [enrollId]: { learningObjects: { [loId]: bytes } } } — tamanho real
    // (não estimado) do diretório offline/{loId}/ medido via Filesystem.readdir
    // logo após o download concluir. LOs baixados ANTES dessa feature têm
    // entry ausente — UI omite o badge nesses casos (degradação graciosa).
    pendingDrmPurge: null,
    // { courseId, courseName, enrollIds: [id], loCount: N } | null — volátil
    // { enrollId, courseName, names: [string], redownloadLoIds: [id] } | null — volátil.
    // Setado pelo reconcileOfflineDownloads quando remove aulas órfãs (produtor
    // trocou o vídeo). O Index.vue observa e abre o OutdatedLessonsNotice.
    pendingOutdatedNotice: null,
    downloadQualityWifi: initial.downloadQualityWifi,
    // 'low' | 'medium' | 'high' | null — qualidade padrão usada quando o download
    // dispara em Wi-Fi. null significa "primeira vez": dispara onboarding modal
    // antes do 1º download de VÍDEO (PDF/outros tipos não acionam o prompt).
    // Persistida via saveState — vira default permanente após primeira escolha.
    downloadQualityLastCellular: initial.downloadQualityLastCellular,
    // 'low' | 'medium' | 'high' | null — última qualidade que o user escolheu
    // numa fila em cellular. Usada APENAS pra pré-selecionar a opção no modal
    // cellular da próxima fila — cellular sempre pergunta. null = nunca baixou
    // em cellular (pré-seleção cai pra 'low' por defensiva).
    showOfflineSyncPanel: initial.showOfflineSyncPanel,
    // Liga o painel de diagnóstico OfflineSyncDebug (fila de sync offline). Default off.
    playbackPriority: initial.playbackPriority
    // Prioridade de reprodução (switch em Configurações de download). Com aula
    // em streaming aberta, batches novos baixam com 1 conexão. Persistida.
  },
  mutations: {
    // Snapshot dos metadados do enroll (nome, imagem, courseId) no momento
    // do download. Sobrevive a revogação do backend — quando o enroll some
    // de state.enroll.enrolls (matrícula cancelada/expirada), o curso ainda
    // aparece em Meus Downloads pra que o user consiga remover os arquivos
    // e liberar espaço. Atualiza a cada cacheEnrollStructure (idempotente).
    SET_DOWNLOAD_META (state, { enrollId, meta }) {
      if (!state.downloads[enrollId]) {
        Vue.set(state.downloads, enrollId, { learningObjects: {}, classes: {} });
      }
      Vue.set(state.downloads[enrollId], 'meta', {
        ...meta,
        capturedAt: Date.now()
      });
      saveState(state);
    },
    SET_STATUS (state, { enrollId, type, id, status }) {
      if (!state.downloads[enrollId]) {
        Vue.set(state.downloads, enrollId, { learningObjects: {}, classes: {} });
      }
      Vue.set(state.downloads[enrollId][type], id, status);
      saveState(state);
    },
    SET_PROGRESS (state, { enrollId, type, id, progress }) {
      if (!state.progress[enrollId]) {
        Vue.set(state.progress, enrollId, { learningObjects: {}, classes: {} });
      }
      if (!state.progress[enrollId][type]) {
        Vue.set(state.progress[enrollId], type, {});
      }
      Vue.set(state.progress[enrollId][type], id, progress);
      saveState(state);
    },
    SET_CACHED (state, { enrollId, value }) {
      Vue.set(state.cachedEnrolls, enrollId, value);
      saveState(state);
    },
    SET_PATH (state, { enrollId, type, id, url }) {
      if (!state.paths[enrollId]) {
        Vue.set(state.paths, enrollId, { learningObjects: {} });
      }
      if (!state.paths[enrollId][type]) {
        Vue.set(state.paths[enrollId], type, {});
      }
      Vue.set(state.paths[enrollId][type], id, url);
      saveState(state);
    },
    // Aplica vários paths de uma vez, persistindo UMA vez só. O SET_PATH serializa
    // o registro INTEIRO a cada chamada; corrigir N paths órfãos no boot fazia N
    // serializações completas do `ead_downloads`.
    SET_PATHS (state, entries) {
      if (!Array.isArray(entries) || !entries.length) return;
      entries.forEach(({ enrollId, type, id, url }) => {
        if (!state.paths[enrollId]) Vue.set(state.paths, enrollId, { learningObjects: {} });
        if (!state.paths[enrollId][type]) Vue.set(state.paths[enrollId], type, {});
        Vue.set(state.paths[enrollId][type], id, url);
      });
      saveState(state);
    },
    SET_FORMAT (state, { enrollId, id, format }) {
      if (!state.formats[enrollId]) {
        Vue.set(state.formats, enrollId, { learningObjects: {} });
      }
      if (!state.formats[enrollId].learningObjects) {
        Vue.set(state.formats[enrollId], 'learningObjects', {});
      }
      Vue.set(state.formats[enrollId].learningObjects, id, format);
      saveState(state);
    },
    // SET_POSITION agora aceita `updatedAt` opcional. Default = Date.now()
    // (play real do user). Quando uma posição é ESTIMADA a partir do
    // backend (%, vinda do enroll grid) e local está vazio, callers passam
    // updatedAt: 0 — funciona como marcador "rehidratado, não preciso", e o
    // ordenamento de Continue Assistindo (por updatedAt desc) naturalmente
    // empurra essas estimativas pro fim da lista (não competem com tempos
    // reais de play recente).
    SET_POSITION (state, { enrollId, loId, seconds, updatedAt }) {
      if (!state.positions[enrollId]) {
        Vue.set(state.positions, enrollId, { learningObjects: {} });
      }
      Vue.set(state.positions[enrollId].learningObjects, loId, {
        seconds,
        updatedAt: typeof updatedAt === 'number' ? updatedAt : Date.now()
      });
      saveState(state);
    },
    SET_MIGRATING (state, value) {
      state.migrating = value;
    },
    SET_PARTIAL (state, { enrollId, loId, progress, quality }) {
      if (!state.partialDownloads[enrollId]) {
        Vue.set(state.partialDownloads, enrollId, {});
      }
      // Preserva retryCount existente ao re-falhar (cada falha consecutiva
      // incrementa). Manual click via downloadLearningObject chama CLEAR_PARTIAL
      // primeiro, então a contagem zera entre tentativas manuais.
      const prev = state.partialDownloads[enrollId][loId];
      Vue.set(state.partialDownloads[enrollId], loId, {
        progress: progress || 0,
        pausedAt: Date.now(),
        retryCount: (prev?.retryCount || 0) + 1,
        // Qualidade do download interrompido. O nome do segmento é POSICIONAL
        // (seg_N.ts) e não codifica o variant, e o reconcile nativo casa por
        // filename — então retomar em outra qualidade marca os seg_N já no disco
        // (ex.: 1080p) como prontos e baixa o resto em 360p, produzindo um vídeo
        // com variants misturados e um manifesto que descreve o variant errado.
        // Sem persistir aqui, o resume caía no default 'low' sempre.
        quality: quality || prev?.quality || null
      });
      saveState(state);
    },
    CLEAR_PARTIAL (state, { enrollId, loId }) {
      if (state.partialDownloads[enrollId] && state.partialDownloads[enrollId][loId]) {
        Vue.delete(state.partialDownloads[enrollId], loId);
        saveState(state);
      }
    },
    SET_DOWNLOAD_ON_CELLULAR (state, value) {
      state.downloadOnCellular = Boolean(value);
      saveState(state);
    },
    SET_SHOW_OFFLINE_SYNC_PANEL (state, value) {
      state.showOfflineSyncPanel = Boolean(value);
      saveState(state);
    },
    SET_PLAYBACK_PRIORITY (state, value) {
      state.playbackPriority = Boolean(value);
      saveState(state);
    },
    SET_DOWNLOAD_QUALITY_WIFI (state, value) {
      // Aceita 'low' | 'medium' | 'high' | null. null reseta pro estado de
      // "primeira vez" (próximo download de vídeo dispara o onboarding modal).
      const valid = ['low', 'medium', 'high'].includes(value) ? value : null;
      state.downloadQualityWifi = valid;
      saveState(state);
    },
    SET_DOWNLOAD_QUALITY_LAST_CELLULAR (state, value) {
      const valid = ['low', 'medium', 'high'].includes(value) ? value : null;
      state.downloadQualityLastCellular = valid;
      saveState(state);
    },
    // list: array [{sigla, nome, filename}] (pode ser [] = "sem legendas",
    // estado final) ou null pra DELETAR a entry (volta a "nunca consultado",
    // que o healMissingSubtitles considera pendente).
    SET_SUBTITLES (state, { enrollId, loId, list }) {
      if (list === null) {
        if (state.subtitles[enrollId] && state.subtitles[enrollId].learningObjects) {
          Vue.delete(state.subtitles[enrollId].learningObjects, loId);
          saveState(state);
        }
        return;
      }
      if (!state.subtitles[enrollId]) {
        Vue.set(state.subtitles, enrollId, { learningObjects: {} });
      }
      Vue.set(state.subtitles[enrollId].learningObjects, loId, list);
      saveState(state);
    },
    SET_SIZE (state, { enrollId, loId, bytes }) {
      if (!state.sizes[enrollId]) {
        Vue.set(state.sizes, enrollId, { learningObjects: {} });
      }
      Vue.set(state.sizes[enrollId].learningObjects, loId, bytes);
      saveState(state);
    },
    CLEAR_SIZE (state, { enrollId, loId }) {
      if (state.sizes[enrollId] && state.sizes[enrollId].learningObjects) {
        Vue.delete(state.sizes[enrollId].learningObjects, loId);
        saveState(state);
      }
    },
    SET_PENDING_DRM_PURGE (state, payload) {
      state.pendingDrmPurge = payload;
    },
    CLEAR_PENDING_DRM_PURGE (state) {
      state.pendingDrmPurge = null;
    },
    SET_PENDING_OUTDATED_NOTICE (state, payload) {
      state.pendingOutdatedNotice = payload;
    },
    CLEAR_PENDING_OUTDATED_NOTICE (state) {
      state.pendingOutdatedNotice = null;
    }
  },
  getters: {
    isLearningObjectDownloaded: (state) => (enrollId, id) =>
      (state.downloads[enrollId]?.learningObjects?.[id] || 'idle') === 'downloaded',

    isClassDownloaded: (state) => (enrollId, id) =>
      (state.downloads[enrollId]?.classes?.[id] || 'idle') === 'downloaded',

    getLearningObjectStatus: (state) => (enrollId, id) =>
      state.downloads[enrollId]?.learningObjects?.[id] || 'idle',

    getClassStatus: (state) => (enrollId, id) =>
      state.downloads[enrollId]?.classes?.[id] || 'idle',

    // Agregado de CURSO. Recebe os loIds de fora porque o store só conhece as
    // aulas que já foram tocadas — quantas o curso TEM é informação do grid, que
    // quem chama tem em mãos.
    //
    // O progresso é por aulas concluídas, não por bytes: num curso que leva
    // dezenas de minutos, "42 de 115" diz mais ao aluno do que a fração do
    // arquivo atual, e é o único número que não anda pra trás quando a aula
    // seguinte é menor que a anterior.
    getEnrollDownloadSummary: (state) => (enrollId, loIds) => {
      const los = state.downloads[enrollId]?.learningObjects || {};
      const ids = loIds || [];
      let done = 0;
      let active = 0;
      ids.forEach(id => {
        const s = los[id];
        if (s === 'downloaded') done++;
        else if (s === 'downloading' || s === 'queued') active++;
      });
      const total = ids.length;
      // Parcial sem nada em voo continua 'idle': o botão volta a dizer "baixar"
      // e o clique pega só o que falta, já que downloadLearningObject pula o que
      // já está baixado.
      const status = total === 0 ? 'idle'
        : done === total ? 'downloaded'
          : active > 0 ? 'downloading'
            : 'idle';
      return { total, done, status, progress: total ? Math.round((done / total) * 100) : 0 };
    },

    getClassProgress: (state) => (enrollId, id) =>
      state.progress[enrollId]?.classes?.[id] || 0,

    getLearningObjectProgress: (state) => (enrollId, id) =>
      state.progress[enrollId]?.learningObjects?.[id] || 0,

    getLocalManifestUrl: (state) => (enrollId, id) =>
      state.paths[enrollId]?.learningObjects?.[id] || null,

    // Formato do arquivo baixado ('hls' | 'audio'). Ausente = legado, tratado
    // como HLS pelo playback (manifest local direto).
    getLocalFormat: (state) => (enrollId, id) =>
      state.formats[enrollId]?.learningObjects?.[id] || null,

    isStructureCached: (state) => (enrollId) =>
      Boolean(state.cachedEnrolls[enrollId]),

    getLocalPosition: (state) => (enrollId, loId) =>
      state.positions[enrollId]?.learningObjects?.[loId]?.seconds || 0,

    getLocalPositionUpdatedAt: (state) => (enrollId, loId) =>
      state.positions[enrollId]?.learningObjects?.[loId]?.updatedAt || 0,

    // Retorna { progress, pausedAt } se o LO tem download parcial preservado
    // (pausado por falha de rede). null caso contrário.
    getPartialDownload: (state) => (enrollId, loId) =>
      state.partialDownloads[enrollId]?.[loId] || null,

    hasPartialDownload: (state) => (enrollId, loId) =>
      Boolean(state.partialDownloads[enrollId]?.[loId]),

    // Progresso "efetivo" pra UI: progresso vivo se estiver baixando, OU o
    // progresso preservado se LO tá pausado por rede, OU 0. Existe pra evitar
    // optional chaining `?.` em template — Vue 2.6 não suporta no compiler.
    getEffectiveProgress: (state) => (enrollId, loId) => {
      const live = state.progress[enrollId]?.learningObjects?.[loId];
      if (live) return live;
      const partial = state.partialDownloads[enrollId]?.[loId];
      return (partial && partial.progress) || 0;
    },

    getClassDownloadedRatio: (state) => (enrollId, loIds) => {
      if (!loIds || !loIds.length) return null;
      const downloaded = loIds.filter(id =>
        (state.downloads[enrollId]?.learningObjects?.[id] || 'idle') === 'downloaded'
      ).length;
      return downloaded === 0 ? null : Math.round((downloaded / loIds.length) * 100);
    },

    // Pref persistente "baixar livremente em cellular" — habilitada via checkbox
    // "Não perguntar novamente" no modal de confirmação cellular.
    getDownloadOnCellular: (state) => state.downloadOnCellular,

    getShowOfflineSyncPanel: (state) => state.showOfflineSyncPanel,

    getPlaybackPriority: (state) => state.playbackPriority,

    // Flag de ciclo da fila (não-reativa, lida no momento do clique). True quando
    // user já confirmou cellular pra essa rajada de downloads. Reset automático
    // no downloadQueue 'idle' (módulo-level acima).
    isCellularConfirmedThisCycle: () => _cellularConfirmedThisCycle,

    getLearningObjectSize: (state) => (enrollId, loId) =>
      state.sizes[enrollId]?.learningObjects?.[loId] || 0,

    // SEM `|| []` de propósito: undefined = nunca consultado (heal retenta),
    // [] = consultado e sem legendas (estado final). Quem consome pra montar
    // tracks trata os dois como "nada a exibir".
    getLocalSubtitles: (state) => (enrollId, loId) =>
      state.subtitles[enrollId]?.learningObjects?.[loId],

    // Soma agregada de bytes de TODOS os LOs baixados (cross-curso). LOs que
    // foram baixados ANTES da feature de sizes (computeOfflineSize) não têm
    // entry — somam zero, subreportando. Aceitável pra fins informativos.
    // Recomputa só quando state.sizes muda (Vuex memoiza).
    totalDownloadedBytes: (state) => {
      let total = 0;
      Object.values(state.sizes || {}).forEach(enrollSizes => {
        const map = (enrollSizes && enrollSizes.learningObjects) || {};
        Object.values(map).forEach(bytes => {
          if (typeof bytes === 'number' && bytes > 0) total += bytes;
        });
      });
      return total;
    },

    // Conta total de LOs com status 'downloaded' (cross-curso). Usado pela
    // página "Meus Downloads" no header agregador.
    totalDownloadedLearningObjects: (state) => {
      let count = 0;
      Object.values(state.downloads || {}).forEach(enrollState => {
        Object.values((enrollState && enrollState.learningObjects) || {}).forEach(status => {
          if (status === 'downloaded') count++;
        });
      });
      return count;
    },

    // Quantos enrolls têm ao menos 1 LO 'downloaded'. Não usa cachedEnrolls
    // porque a flag persiste mesmo sem LOs concluídos (cache só de estrutura).
    totalDownloadedCourses: (state) => {
      return Object.values(state.downloads || {}).filter(enrollState =>
        Object.values((enrollState && enrollState.learningObjects) || {})
          .some(s => s === 'downloaded')
      ).length;
    },

    // Lista de cursos com pelo menos 1 LO baixado, prontos pra renderização.
    //
    // Itera state.downloads (não rootState.enroll.enrolls) porque o enroll
    // pode sumir da lista do backend após revogação de acesso (matrícula
    // cancelada/refund/expirada). Quando isso acontece, queremos que o curso
    // CONTINUE aparecendo em Meus Downloads — com badge "Acesso suspenso" —
    // pra que o user consiga remover os arquivos do disco. Sem isso, ficariam
    // GBs órfãos invisíveis e irremovíveis.
    //
    // Cadeia de fallbacks pra metadados (nome/imagem/courseId):
    //   1. rootState.enroll.enrolls — fonte autoritativa (user tem acesso)
    //   2. state.downloads[X].meta — snapshot persistente (sobrevive a revoke)
    //   3. offlineStorage.getGrid — fallback adicional (também sobrevive)
    //   4. Placeholder "Curso #<enrollId>" — pior caso (perdeu tudo)
    //
    // hasAccess false só quando a lista de enrolls é fresca E o enroll está
    // ausente (revogação real). Lista não-fresca → assume acesso (vide abaixo).
    downloadedCourses: (state, getters, rootState) => {
      const enrolls = (rootState.enroll && rootState.enroll.enrolls) || [];
      const enrollsFresh = !!(rootState.enroll && rootState.enroll.enrollsFresh);
      const enrollMap = new Map(enrolls.map(e => [String(e.id), e]));
      const currentStudentId = rootState.user && rootState.user.currentUser && rootState.user.currentUser.studentId;

      return Object.keys(state.downloads)
        .map(enrollId => {
          const enrollState = state.downloads[enrollId];
          if (!enrollState) return null;

          const downloadedLoIds = new Set();
          Object.entries(enrollState.learningObjects || {})
            .forEach(([loId, status]) => {
              if (status === 'downloaded') downloadedLoIds.add(String(loId));
            });
          if (downloadedLoIds.size === 0) return null;

          // Resolução de metadados via cadeia de fallbacks
          const enroll = enrollMap.get(String(enrollId));
          const meta = enrollState.meta || null;
          const grid = offlineStorage.getGrid(enrollId);

          // Grid é necessário pra estruturar os groups (módulos/disciplinas)
          // — sem ele, só conseguimos mostrar o card "vazio" com nome+image.
          // Pra cursos órfãos sem grid (caso raro: revogação + clearCache do
          // localStorage), montamos um fake grid com lista plana de LOs sem
          // estrutura, baseada nas keys de state.downloads. Aceitável pra
          // permitir remoção; user não vai navegar mesmo.
          let visualGroups;
          if (grid) {
            const sizesByLoId = (state.sizes[enrollId] || {}).learningObjects || {};
            visualGroups = flattenGridForDownloads(grid).groups
              .map(group => {
                const filteredLOs = group.learningObjects
                  .filter(lo => downloadedLoIds.has(String(lo.id)))
                  .map(lo => ({ ...lo, size: sizesByLoId[lo.id] || 0 }));
                const totalSize = filteredLOs.reduce((acc, lo) => acc + lo.size, 0);
                return { ...group, learningObjects: filteredLOs, totalSize };
              })
              .filter(group => group.learningObjects.length > 0);
          } else {
            // Sem grid (caso edge: localStorage parcialmente perdido).
            // Cria grupo flat só pra exibir contagem + permitir remover.
            const sizesByLoId = (state.sizes[enrollId] || {}).learningObjects || {};
            const flatLOs = Array.from(downloadedLoIds).map(loId => ({
              id: loId,
              name: `Aula ${loId}`,
              size: sizesByLoId[loId] || 0
            }));
            const totalSize = flatLOs.reduce((acc, lo) => acc + lo.size, 0);
            visualGroups = flatLOs.length
              ? [{ kind: 'class', name: 'Aulas baixadas', learningObjects: flatLOs, totalSize }]
              : [];
          }

          if (visualGroups.length === 0) return null;

          const totalLearningObjects = visualGroups.reduce((acc, g) => acc + g.learningObjects.length, 0);
          const totalSize = visualGroups.reduce((acc, g) => acc + g.totalSize, 0);

          const name = (grid && grid.name) ||
            (meta && meta.name) ||
            (enroll && (enroll.courseName || enroll.name)) ||
            `Curso #${enrollId}`;
          const image = (grid && (grid.image || grid.logo)) ||
            (meta && meta.image) ||
            (enroll && enroll.image) ||
            null;

          // Dono do download (carimbado no meta). 'Outra conta' só quando SABEMOS
          // o dono E ele difere do logado — legados sem studentId ou match contam
          // como da conta atual (não esconde o que é do próprio user).
          const ownerStudentId = meta && meta.studentId;
          const isOtherAccount = !!(currentStudentId && ownerStudentId &&
            String(ownerStudentId) !== String(currentStudentId));

          return {
            enrollId,
            name,
            image,
            groups: visualGroups,
            totalLearningObjects,
            totalSize,
            // false = revogação real (badge "Acesso suspenso"); remoção segue
            // funcionando, playback já é bloqueado pelo lease.
            hasAccess: !!enroll || !enrollsFresh,
            isOtherAccount
          };
        })
        .filter(Boolean);
    },

    // Top 10 LOs efetivamente baixados (status downloaded) com seconds > 0,
    // ordenados por updatedAt desc — alimenta a fileira "Continue assistindo".
    // Filtra concluídos (margem de 3s sobre a duração quando disponível).
    continueWatchingLearningObjects: (state, getters, rootState) => {
      const enrolls = (rootState.enroll && rootState.enroll.enrolls) || [];
      const enrollMap = new Map(enrolls.map(e => [String(e.id), e]));
      const list = [];

      Object.entries(state.positions || {}).forEach(([enrollId, types]) => {
        const enroll = enrollMap.get(String(enrollId));
        if (!enroll) return;
        const grid = offlineStorage.getGrid(enrollId);
        if (!grid) return;

        Object.entries((types && types.learningObjects) || {}).forEach(([loId, pos]) => {
          const seconds = (pos && pos.seconds) || 0;
          if (seconds <= 0) return;

          const status = state.downloads[enrollId]?.learningObjects?.[loId];
          if (status !== 'downloaded') return;

          const lo = findLO(grid, loId);
          if (!lo) return;

          const duration = (lo.broadcast && lo.broadcast.duration) || lo.seconds || lo.duration || 0;
          if (duration && seconds >= duration - 3) return;

          list.push({
            enrollId,
            loId,
            name: lo.name || lo.title,
            courseName: grid.name || enroll.courseName || enroll.name,
            thumbnail: lo.image || null,
            seconds,
            duration,
            updatedAt: (pos && pos.updatedAt) || 0
          });
        });
      });

      list.sort((a, b) => b.updatedAt - a.updatedAt);
      return list.slice(0, 10);
    }
  },
  actions: {
    // Retoma todos os downloads em estado paused-offline. Chamado pelo
    // listener de rede no main.js quando conectividade volta (e também no
    // Network.getStatus() no boot). Cada LO entra na PQueue interna do
    // download.js (concurrency=1), então a fila processa serial — mesmo se 20
    // LOs estão pausados, não satura rede com 20 batches simultâneos.
    //
    // payload.connectionType: 'wifi' | 'cellular' | 'none' | undefined
    // Auto-resume só dispara em wifi por default. User economiza dados móveis;
    // clicar manual no ícone âmbar ignora essa restrição (intenção explícita).
    //
    // Cada partial tem retryCount que incrementa a cada falha de rede
    // consecutiva. Acima de MAX_AUTO_RETRY, o partial vira "frozen" — auto-
    // resume ignora, click manual sempre tenta (CLEAR_PARTIAL no início zera
    // o counter).
    //
    // Idempotente: rodar 2x não dispara downloads duplicados (downloadLearningObject
    // commit SET_STATUS('downloading') na primeira chamada; segunda chega no
    // plugin com batchId já RUNNING e early-returns com alreadyRunning=true).
    //
    // Cenário onde falha silenciosamente: user reconectou em outra tela e
    // rootState.enroll.current não tem o curso correspondente. downloadLearningObject
    // não encontra o learningObject → early return → partial fica intacto.
    // Próxima reconexão (ou clique manual no ícone âmbar) tenta de novo.
    // Reconcilia batches que o NATIVO retomou sozinho no boot com o estado do JS.
    //
    // O problema: no load do plugin, as duas plataformas auto-retomam todo batch
    // com state=running e itens pendentes (Android recoverPendingBatches → resobe
    // o ForegroundService; iOS reconnectTasksAndResumePending → reagenda tasks).
    // O JS faz o OPOSTO no boot: rebaixa 'downloading'/'queued' → 'idle'. E o
    // marcador de parcial só é criado no catch de erro de rede — num kill de
    // processo nenhum catch roda, então não existe marcador.
    //
    // Resultado: o nativo baixava os segmentos restantes (consumindo dados do
    // usuário), emitia batchCompleted para um batchId SEM assinante no event bus,
    // e a etapa que escreve o manifest.m3u8 + SET_PATH + SET_STATUS('downloaded')
    // nunca rodava. Ficava o vídeo inteiro no disco com a UI dizendo "não
    // baixado" — e sem marcador, removeAllFromEnroll (que filtra != 'idle') não
    // removia: lixo permanente, invisível e irremovível.
    //
    // A correção NÃO cria um segundo caminho de retomada: cancela o batch órfão
    // (cancel só para o serviço, NÃO apaga os segmentos já baixados) e cria o
    // marcador de parcial. Assim o órfão entra no caminho normal já testado —
    // resumePausedDownloads, que respeita wifi-only, conta retry, preserva a
    // qualidade e finaliza direito. Precisa rodar ANTES do resume.
    async reconcileOrphanBatches ({ state, commit }) {
      if (!Capacitor.isNativePlatform()) return;
      let batchIds = [];
      try {
        const res = await VxlBatchDownloader.listActiveBatches();
        batchIds = (res && res.batchIds) || [];
      } catch (e) {
        console.warn('[download] listActiveBatches falhou:', e?.message || e);
        return;
      }
      if (!batchIds.length) return;

      for (const batchId of batchIds) {
        // batchId = `<tipo>_<safeLoId>` (lo_/audio_/pdf_ — ver batchIdFor
        // em cada downloader).
        const match = String(batchId).match(/^(?:lo|audio|pdf)_(.+)$/);
        if (!match) continue;
        const safeId = match[1];

        // Descobre o enroll dono: o batchId não carrega o enrollId. Compara pelo
        // loId SANITIZADO, que é como o batchId foi montado.
        let ownerEnrollId = null;
        let ownerLoId = null;
        for (const [enrollId, types] of Object.entries(state.downloads || {})) {
          const found = Object.keys((types && types.learningObjects) || {})
            .find(loId => String(loId).replace(/[^A-Za-z0-9_-]/g, '_') === safeId);
          if (found) { ownerEnrollId = enrollId; ownerLoId = found; break; }
        }

        try {
          await VxlBatchDownloader.cancel({ batchId });
        } catch (e) {
          console.warn('[download] cancel de batch órfão falhou:', batchId, e?.message || e);
        }

        if (ownerEnrollId && ownerLoId) {
          // Marcador de parcial: a UI passa a oferecer "Continuar" e o
          // resumePausedDownloads (logo depois, no boot) retoma pelo caminho
          // normal. O reconcileExistingFiles do nativo reaproveita o que já está
          // no disco, então nada do que foi baixado se perde.
          const already = state.partialDownloads
            && state.partialDownloads[ownerEnrollId]
            && state.partialDownloads[ownerEnrollId][ownerLoId];
          if (!already) {
            const prog = state.progress
              && state.progress[ownerEnrollId]
              && state.progress[ownerEnrollId].learningObjects;
            commit('SET_PARTIAL', {
              enrollId: ownerEnrollId,
              loId: ownerLoId,
              progress: (prog && prog[ownerLoId]) || 0,
              quality: null
            });
          }
        } else {
          // Sem dono no state (curso removido enquanto o app estava morto): o
          // batch não tem pra onde voltar. Limpa o registro nativo pra não ser
          // re-retomado a cada boot.
          try {
            await VxlBatchDownloader.cleanupBatch({ batchId });
          } catch (e) {
            console.warn('[download] cleanupBatch órfão falhou:', batchId, e?.message || e);
          }
        }
      }
    },

    // Conserta `paths` que apontam para um container que não existe mais.
    //
    // O path é gravado ABSOLUTO no download (convertFileSrc de um getUri), e o
    // caminho do container de dados não é eterno: muda em restore de backup,
    // migração e reinstalação. Quando muda, todo download vira um path órfão —
    // e o sintoma é idêntico ao do índice perdido: aula marcada como baixada,
    // player preto, e sem cair pro online (o localVideoSrc prefere a fonte
    // local enquanto o status for 'downloaded'). Só que aqui o erro é
    // MEDIA_ERR_SRC_NOT_SUPPORTED em vez de 403.
    //
    // Reescreve apenas o PREFIXO, preservando o nome do arquivo gravado
    // (manifest.m3u8 / audio.mp3 / document.pdf) — assim não é
    // preciso saber o tipo de mídia, e um sufixo como `#t=10` sobrevive.
    //
    // No-op quando o container não mudou: se o path já começa com a base atual,
    // nada é reescrito. Sem isso, uma diferença cosmética entre o que geramos
    // aqui e o que o downloader gera reescreveria tudo a cada boot.
    async healOfflinePaths ({ state, commit }) {
      if (!Capacitor.isNativePlatform()) return 0;

      let base;
      try {
        const { uri } = await Filesystem.getUri({ path: OFFLINE_DIR, directory: STORAGE_DIR });
        base = Capacitor.convertFileSrc(uri).replace(/\/+$/, '');
      } catch (e) {
        console.warn('[download] healOfflinePaths: base indisponivel:', e?.message || e);
        return 0;
      }
      if (!base) return 0;

      const fixes = [];
      Object.entries(state.downloads || {}).forEach(([enrollId, rec]) => {
        const los = (rec && rec.learningObjects) || {};
        const paths = (state.paths[enrollId] || {}).learningObjects || {};
        Object.entries(los).forEach(([loId, status]) => {
          if (status !== 'downloaded') return;
          const stored = paths[loId];
          if (!stored || stored.startsWith(base + '/')) return;

          const name = stored.substring(stored.lastIndexOf('/') + 1);
          // Sem nome de arquivo reconhecível não dá pra reconstruir com
          // segurança — melhor deixar quebrado do que apontar pra lugar errado.
          if (!name || name.indexOf('.') < 0) return;

          fixes.push({
            enrollId,
            type: 'learningObjects',
            id: loId,
            url: `${base}/${safeLoId(loId)}/${name}`
          });
        });
      });

      if (!fixes.length) return 0;

      commit('SET_PATHS', fixes);
      console.warn(`[download] paths offline corrigidos: ${fixes.length} (container mudou de lugar)`);
      return fixes.length;
    },

    // Reconstrói o índice nativo loId->enrollId a partir do estado do JS, que é
    // o dono autoritativo desse mapeamento.
    //
    // Por que precisa existir: o gate de chave é fail-CLOSED — sem entrada no
    // índice ele nega a chave AES, e a aula baixada vira player preto com
    // spinner eterno. E não toca nem online, porque o `localVideoSrc` prefere a
    // fonte local sempre que o status é 'downloaded'. Foi o que aconteceu no
    // salto 1.0.6 -> 1.1.0, quando dois `skip` documentados do gate viraram 403.
    // Nunca conseguimos provar como o índice se perdeu naquele device; este heal
    // torna a pergunta irrelevante, porque cobre qualquer causa.
    //
    // Só entram LOs com mídia CIFRADA (`hls`). Áudio é plano e texto/quiz
    // não têm arquivo — nenhum dos dois usa chave, e registrá-los sujaria o
    // índice com associações que o handler nunca consulta.
    async healLoEnrollIndex ({ state, rootState }) {
      if (!Capacitor.isNativePlatform()) return 0;

      const map = {};
      // Só serve pra pular matrícula que claramente é de outra conta; ausência
      // NÃO impede o heal. Diferente do lease, um par errado aqui não concede
      // acesso: quem autoriza é o lease (bound ao studentId) e a File Key, que
      // só existe no keystore se este device baixou o conteúdo.
      const studentId = rootState.user?.currentUser?.studentId;

      Object.entries(state.downloads || {}).forEach(([enrollId, rec]) => {
        const owner = rec && rec.meta && rec.meta.studentId;
        if (studentId && owner && String(owner) !== String(studentId)) return;

        const los = (rec && rec.learningObjects) || {};
        const formats = (state.formats[enrollId] || {}).learningObjects || {};
        const paths = (state.paths[enrollId] || {}).learningObjects || {};
        Object.entries(los).forEach(([loId, status]) => {
          if (status !== 'downloaded') return;

          // Só mídia CIFRADA precisa de entrada no índice — áudio é plano e
          // texto/quiz não têm arquivo; registrá-los sujaria o índice com
          // associações que o handler nunca consulta.
          //
          // `formats` só existe desde 09/jun, e download anterior a isso não
          // tem o campo — justamente a população com maior chance de estar sem
          // índice. Por isso o fallback pelo nome do arquivo, que vem do
          // SET_PATH (13/mai) e cobre todo o histórico.
          const format = formats[loId];
          const encrypted = format === 'hls' ||
            (!format && /\/manifest\.m3u8(\?|#|$)/.test(paths[loId] || ''));
          if (!encrypted) return;
          // safeLoId: mesma forma usada no keyAlias do enqueue e na URI
          // vxlkey://<safeId> do manifesto. Gravar o id cru criaria uma chave
          // que o handler nunca encontraria.
          const key = safeLoId(loId);
          if (key) map[key] = String(enrollId);
        });
      });

      if (!Object.keys(map).length) return 0;

      try {
        const res = await VxlBatchDownloader.setLoEnrollMap({ map });
        const changed = (res && res.changed) || 0;
        if (changed > 0) {
          console.warn(`[download] indice loId->enrollId reconstruido: ${changed} associacao(oes)`);
        }
        return changed;
      } catch (e) {
        // Build nativo antigo (sem o método) ou falha de bridge: não pode
        // derrubar o boot. O pior caso é o comportamento de hoje.
        console.warn('[download] healLoEnrollIndex falhou:', e?.message || e);
        return 0;
      }
    },

    // Re-busca a IA das aulas baixadas que ficaram sem ai.json.
    //
    // A pré-busca do download é fire-and-forget: se ela falhar (5xx, rede caindo
    // no meio) ou o app morrer entre o dispatch e a escrita, a aula fica
    // 'downloaded' sem IA e offline a aba aparece vazia sem explicação — não há
    // nada no fluxo que tente de novo. Abrir a aula COM rede recarrega, mas quem
    // baixou e só abriu offline nunca chega lá.
    //
    // No caso normal isto não faz request nenhuma: só verifica a existência do
    // arquivo. O teto existe porque um aparelho com centenas de aulas baixadas e
    // um incidente no endpoint dispararia uma rajada na volta da rede; o que
    // sobrar é retomado na próxima reconexão.
    // Baixa as legendas CC (.vtt) da aula pro diretório offline e grava o
    // índice no store. Fire-and-forget no fluxo de download: legenda faltando
    // NUNCA pode marcar como falho um download cujo vídeo baixou 100%.
    //
    // Distinção que o heal explora: falha na CONSULTA (/info inacessível) não
    // commita nada — a entry fica undefined e o healMissingSubtitles retenta
    // no próximo boot com rede. Consulta OK com zero legendas commita [] —
    // estado final, nunca re-consultado.
    async prefetchSubtitles ({ commit }, { enrollId, loId, broadcastId, token, customerId }) {
      try {
        const legendas = await fetchSubtitleList(broadcastId, token, customerId);
        if (legendas === null) return;
        const saved = await downloadSubtitleFiles(loId, legendas);
        // Consulta OK mas algum download individual falhou: grava só o que
        // salvou QUANDO salvou algo; se nada salvou de N esperadas, deixa
        // undefined pro heal tentar de novo (rede pode ter caído no meio).
        if (legendas.length > 0 && saved.length === 0) return;
        commit('SET_SUBTITLES', { enrollId, loId, list: saved });
      } catch (e) {
        console.warn('[download] prefetchSubtitles falhou:', e?.message || e);
      }
    },

    // Repõe legendas de aulas baixadas sem entry no índice (baixadas antes da
    // feature, ou cuja consulta ao /info falhou no download). Espelho do
    // healMissingAiCache logo abaixo; MAX_PER_RUN limita a rajada por boot.
    async healMissingSubtitles ({ state, rootState, dispatch }) {
      if (!Capacitor.isNativePlatform()) return 0;
      if (!rootState.dom || !rootState.dom.isConnected) return 0;

      const MAX_PER_RUN = 20;
      const enrolls = (rootState.enroll && rootState.enroll.enrolls) || [];
      const enrollMap = new Map(enrolls.map(e => [String(e.id), e]));

      let healed = 0;
      for (const [enrollId, rec] of Object.entries(state.downloads || {})) {
        if (healed >= MAX_PER_RUN) break;
        const los = (rec && rec.learningObjects) || {};
        const formats = (state.formats[enrollId] || {}).learningObjects || {};
        const subs = (state.subtitles[enrollId] || {}).learningObjects || {};

        // Credenciais do tenant vivem no enroll; sem ele na lista (revogado,
        // lista ainda não carregada), pula — retenta no próximo boot.
        const enroll = enrollMap.get(String(enrollId));
        const webcast = (enroll && enroll.webcastIntegration) || {};
        if (!webcast.accessToken || !webcast.applicationId) continue;

        // broadcastId vem do grid cacheado — mesma fonte do playback offline.
        const grid = offlineStorage.getGrid(enrollId);
        if (!grid) continue;

        for (const [loId, status] of Object.entries(los)) {
          if (healed >= MAX_PER_RUN) break;
          if (status !== 'downloaded') continue;
          if (formats[loId] !== 'hls') continue;
          if (subs[loId] !== undefined) continue;

          const lo = findLO(grid, loId);
          const broadcastId = lo && lo.broadcast && lo.broadcast.id;
          if (!broadcastId) continue;

          dispatch('prefetchSubtitles', {
            enrollId,
            loId,
            broadcastId,
            token: webcast.accessToken,
            customerId: webcast.applicationId
          });
          healed++;
        }
      }
      if (healed > 0) console.warn(`[download] legendas re-solicitadas para ${healed} aula(s) baixada(s) sem índice`);
      return healed;
    },

    async healMissingAiCache ({ state, rootState, dispatch }) {
      if (!Capacitor.isNativePlatform()) return 0;
      if (!rootState.dom || !rootState.dom.isConnected) return 0;

      const MAX_PER_RUN = 20;
      const pending = [];

      // Sem gate de enableIA aqui, ao contrário da pré-busca. A única fonte
      // disponível neste ponto seria `rootState.enroll.enrolls`, e o endpoint de
      // LISTA devolve settings degradadas: `enableIA`, `subtitleType` e
      // `showProgress` vêm false e `courseId` vem 0, mesmo quando o detalhe do
      // mesmo enroll traz true. O Trustflix já contorna isso buscando o enroll
      // completo. Gatear pela lista zerava o heal inteiro.
      //
      // Custa pouco não gatear: só entram aulas JÁ baixadas e sem ai.json, e o
      // endpoint sempre responde 200 — cada uma é curada uma vez e some da lista.
      Object.entries(state.downloads || {}).forEach(([enrollId, rec]) => {
        const los = (rec && rec.learningObjects) || {};
        const formats = (state.formats[enrollId] || {}).learningObjects || {};
        Object.entries(los).forEach(([loId, status]) => {
          if (status !== 'downloaded') return;
          // Só vídeo: a aba de IA exige aula com broadcast, e é o mesmo recorte
          // que a pré-busca usa. `formats` distingue vídeo de PDF/áudio sem
          // precisar do grid em mãos.
          if (formats[loId] !== 'hls') return;
          pending.push(loId);
        });
      });

      if (!pending.length) return 0;

      let healed = 0;
      for (const loId of pending) {
        if (healed >= MAX_PER_RUN) break;
        // Erro de stat conta como "tem": preferimos não pedir do que disparar
        // request por causa de uma falha de filesystem.
        const has = await offlineStorage.hasAI(loId).catch(() => true);
        if (has) continue;
        dispatch('learningObject/getAI', { LearningObjectId: loId, persistOnly: true }, { root: true });
        healed++;
      }
      if (healed > 0) console.warn(`[download] IA re-solicitada para ${healed} aula(s) baixada(s) sem cache`);
      return healed;
    },

    resumePausedDownloads ({ state, dispatch, commit, rootGetters }, payload = {}) {
      if (!Capacitor.isNativePlatform()) return;
      // Guard de auth: sem token válido, fetchManifestInfo vai 401 e o
      // interceptor http.init.js pode disparar refreshTokens enquanto o boot
      // ou listener já está fazendo refresh — race condition que termina em
      // clearToLogin() apagando tokens e mandando user pra tela de login.
      // Sai silencioso; chamado de novo após auth completar (boot/listener).
      if (!rootGetters['auth/isAuthenticated']) {
        console.log('[download] resume skipped: not authenticated yet');
        return;
      }

      const connectionType = payload.connectionType;
      // Boot offline ou listener disparando com rede ainda ausente: não tenta
      // resumir. Sem esse guard, cada LO eligível ia pro batch, falhava
      // instantâneo por rede, e o SET_PARTIAL incrementava retryCount. 5 boots
      // offline = MAX_AUTO_RETRY → todos os partials viram "frozen" e o user
      // tem que tocar manual em cada um pra destravar. 'unknown' segue (não
      // sabemos, preferimos tentar).
      if (connectionType === 'none') return;

      const isCellular = connectionType === 'cellular';

      // Snapshot — evita mutação durante iteração (downloadLearningObject
      // commit CLEAR_PARTIAL no início) e separa por status (frozen/eligible).
      const eligible = [];
      const frozen = [];
      Object.entries(state.partialDownloads || {}).forEach(([enrollId, los]) => {
        Object.entries(los || {}).forEach(([loId, partial]) => {
          if ((partial.retryCount || 0) >= MAX_AUTO_RETRY) {
            frozen.push({ enrollId, loId });
          } else {
            // `quality` do parcial: retomar noutra qualidade mistura variants
            // (ver comentário em SET_PARTIAL). null cai no default do downloader.
            eligible.push({ enrollId, loId, quality: partial.quality || null });
          }
        });
      });

      // Nada pra retomar.
      if (eligible.length === 0 && frozen.length === 0) return;

      // Bloqueia auto-resume em cellular quando user não optou explicitamente.
      // `_cellularConfirmedThisCycle` também desbloqueia (confirmação one-shot
      // sem persistir a preferência); sem esse check, o resume disparado pela
      // própria `confirmCellularDownloadInThisCycle` cairia no showCellularBlocked.
      // Toast informativo único — compartilha throttle com showNetworkPaused
      // (mesmo evento de raiz: rede indisponível ou inadequada) pra não spammar.
      if (isCellular && !state.downloadOnCellular && !_cellularConfirmedThisCycle && eligible.length > 0) {
        showCellularBlocked(commit);
        return;
      }

      if (eligible.length === 0) {
        // Só tinha frozens — não dispara nada nem mostra toast (frozen já
        // teve seu toast quando atingiu o limite via showNetworkPaused).
        return;
      }

      commit('toast/NEW', {
        type: 'info',
        message: eligible.length === 1
          ? 'Conexão restabelecida. Retomando download.'
          : `Conexão restabelecida. Retomando ${eligible.length} downloads.`,
        duration: 4000
      }, { root: true });

      // Fire-and-forget: cada dispatch entra na PQueue interna. Erros (rede de
      // novo, missing webcast credentials, learningObject não encontrado) caem
      // no catch do downloadLearningObject e marcam partial de novo OU
      // retornam early — sem interferir nas próximas iterações.
      // `auto: true` — sinaliza retomada automática. Sem isso o
      // downloadLearningObject limpava o marcador parcial no início, zerando o
      // retryCount a cada tentativa: MAX_AUTO_RETRY nunca era atingido e um
      // celular com rede piscando (metrô/elevador) re-disparava os downloads
      // indefinidamente, queimando bateria e dados.
      for (const { enrollId, loId, quality } of eligible) {
        dispatch('downloadLearningObject', { enrollId, id: loId, quality, auto: true });
      }
    },

    // Marca que o user confirmou baixar em cellular. Quando `persist` é true,
    // grava a decisão em state.downloadOnCellular (localStorage via saveState),
    // fazendo com que o modal nunca mais apareça e que o auto-resume passe a
    // funcionar em cellular sem perguntar. Quando false, vale só pra fila atual.
    confirmCellularDownloadInThisCycle ({ commit, dispatch, rootState }, payload = {}) {
      const { persist = false } = payload;
      if (persist) commit('SET_DOWNLOAD_ON_CELLULAR', true);
      _cellularConfirmedThisCycle = true;

      // Destrava downloads que foram pausados pelo enforcement wifi-only nativo
      // (allowsCellularAccess=false no iOS / ConnectivityManager no Android).
      // O setting `network` foi capturado em snapshot no enqueue inicial e o
      // plugin nativo não tem API pra atualizá-lo mid-batch; a forma de retomar
      // é re-disparar `downloadLearningObject`, que re-avalia `network` (agora
      // undefined porque `_cellularConfirmedThisCycle` é true) e reenfileira.
      // Só vale se estamos efetivamente em cellular — em Wi-Fi não há nada
      // pausado por wifi-only, e o resume já roda no listener de reconexão.
      const connectionType = rootState.dom && rootState.dom.connectionType;
      if (connectionType === 'cellular') {
        dispatch('resumePausedDownloads', { connectionType });
      }
    },

    // Purga o cache offline pre-cripto quando loadState detectou schema antigo.
    // Ordem importa:
    //   1. nukeAll do plugin nativo: cancela batches que o load() do plugin
    //      ressuscitou + apaga _batchdl_state/*.json + apaga chaves órfãs.
    //      Sem isso, o ForegroundService Android / URLSession iOS estariam
    //      gravando segmentos plaintext concorrentes com o rmdir abaixo.
    //   2. Filesystem.rmdir offline/: remove arquivos baixados pre-cripto.
    //   3. SET_MIGRATING(false): libera downloads no UI (guard em
    //      downloadLearningObject/downloadClass).
    // Chamado uma única vez no boot pelo main.js. Idempotente.
    async migrateLegacyDownloads ({ commit, state }) {
      if (!_legacyMigrationNeeded) {
        // state.migrating já é false se não há migração pendente — fast path.
        if (state.migrating) commit('SET_MIGRATING', false);
        return;
      }
      _legacyMigrationNeeded = false;
      if (!Capacitor.isNativePlatform()) {
        commit('SET_MIGRATING', false);
        return;
      }

      try {
        // 1. Cancela qualquer batch que o plugin tenha ressuscitado e limpa estados.
        // Sem isso, race com rmdir → IOException em writes em flight.
        try {
          await VxlBatchDownloader.nukeAll();
        } catch (e) {
          console.warn('[download] nukeAll falhou (segue mesmo assim):', e?.message || e);
        }

        // 2. Purga fisicamente os arquivos pre-cripto.
        try {
          await Filesystem.rmdir({
            path: 'offline',
            directory: Directory.Data,
            recursive: true
          });
          console.log('[download] offline/ purgado (cleanup pre-cripto)');
        } catch (e) {
          // ENOENT (diretório não existe) é benigno; outros erros loggam mas
          // não bloqueiam — pior caso: lixo plaintext fica no disco até remove
          // manual, mas o user não consegue tocar (sem entry no store.paths).
          console.warn('[download] cleanup pre-cripto falhou:', e?.message || e);
        }
      } finally {
        // SEMPRE liberar. Se algum erro acima escapar (raro, mas defensivo),
        // o user fica preso sem conseguir baixar até reinstalar.
        commit('SET_MIGRATING', false);
      }
    },

    async cacheEnrollStructure ({ commit, dispatch, rootState }, { enrollId }) {
      if (!Capacitor.isNativePlatform()) return;

      const fullGrid = rootState.enroll.current;
      const enrolls = rootState.enroll.enrolls;

      if (!fullGrid || String(fullGrid.id) !== String(enrollId)) return;

      await dispatch('reconcileOfflineDownloads', { enrollId, grid: fullGrid });

      offlineStorage.saveGrid(enrollId, fullGrid);
      if (enrolls && enrolls.length) {
        offlineStorage.saveEnrolls({ items: enrolls });
      }
      commit('SET_CACHED', { enrollId, value: true });

      // Snapshot dos metadados do enroll. Sobrevive à revogação do backend
      // (matrícula cancelada → enroll some de state.enroll.enrolls). Sem esse
      // snapshot, o curso desaparece de Meus Downloads e o user fica sem
      // como remover os GBs órfãos do disco. Procura primeiro no grid; cai
      // pro enroll bruto da listagem caso o grid não tenha.
      const enrollFromList = enrolls.find(e => String(e.id) === String(enrollId));
      commit('SET_DOWNLOAD_META', {
        enrollId,
        meta: {
          name: fullGrid.name || enrollFromList?.name || enrollFromList?.courseName || null,
          image: fullGrid.image || fullGrid.logo || enrollFromList?.image || null,
          courseId: fullGrid.courseId || enrollFromList?.courseId || null,
          // Carimba o dono do download pra separar, em Meus Downloads, o que é
          // da conta logada vs. baixado por outra conta no mesmo device.
          studentId: (rootState.user && rootState.user.currentUser && rootState.user.currentUser.studentId) || null
        }
      });

      // Primeiro download do curso gera o lease inicial. Touch é idempotente,
      // então re-cachear depois (ex: refresh do grid) também renova.
      dispatch('lease/touch', { enrollId }, { root: true }).catch(() => {});

      // Rehidrata positions a partir do progresso do backend. Cobre o caso
      // "aula já tinha progresso histórico (backend conhece) mas local estava
      // vazio (positions não populado)" — sem isso, Meus Downloads mostrava
      // aulas baixadas como não-tocadas e resume offline recomeçava do zero.
      // Roda DEPOIS do cache porque depende do grid já salvo.
      dispatch('syncPositionsFromGrid', { enrollId, grid: fullGrid });

      // Só as imagens que AINDA não estão mapeadas. collectImageUrls devolve
      // todas as do grid, e o cacheImage de uma já-cacheada custa 2 travessias da
      // ponte Capacitor (stat + getUri). Num curso de 13 imagens isso era 26
      // chamadas a CADA clique de download — e a ponte é serializada, então o
      // enqueue/getFreeDiskSize do download ficava na fila atrás delas,
      // atrasando o início do progresso. Com o filtro, curso já cacheado = 0.
      const imageMap = offlineStorage.getImageMap() || {};
      const imageUrls = collectImageUrls(fullGrid, enrolls).filter(url => !imageMap[url]);
      if (!imageUrls.length) return;

      Promise.all(imageUrls.map(url => imageCache.cacheImage(url)))
        .then(localUrls => {
          const map = {};
          imageUrls.forEach((url, i) => { if (localUrls[i]) map[url] = localUrls[i]; });
          if (Object.keys(map).length) offlineStorage.mergeImageMap(map);
        });
    },

    // Detecta e limpa downloads órfãos: aulas baixadas cujo loId NÃO está mais no
    // grid fresco. Acontece quando o produtor troca o vídeo de uma aula — o backend
    // muda o loId (e o broadcastId), então o loId antigo some do grid e o download
    // (arquivos + state + File Key, tudo chaveado por loId) ficaria órfão pra sempre,
    // com o aluno assistindo offline o vídeo desatualizado.
    //
    // IMPORTANTE: só rodar com `grid` FRESCO/autoritativo (online, curso completo).
    // Reconciliar contra um grid stale/parcial/preview removeria downloads válidos.
    // Por isso é chamado só de contextos online (cacheEnrollStructure no download,
    // setCurrentEnroll online não-preview) + guard defensivo de conectividade.
    async reconcileOfflineDownloads ({ state, dispatch, commit }, { enrollId, grid }) {
      if (!Capacitor.isNativePlatform() || !grid) return;
      if (!this.state.dom.isConnected) return; // defensivo: nunca reconciliar offline

      const validLoIds = new Set(
        collectClassesFromGrid(grid)
          .flatMap(c => (c.learningObjects || []).map(lo => String(lo.id)))
      );

      // Salvaguarda: grid sem NENHUMA aula é quase certamente uma resposta
      // ruim/parcial do backend (não "todas as aulas foram deletadas").
      // Reconciliar nesse caso removeria TODOS os downloads do enroll por engano.
      if (!validLoIds.size) return;

      const enrollLos = (state.downloads[enrollId] || {}).learningObjects || {};
      const orphans = Object.keys(enrollLos)
        .filter(loId => enrollLos[loId] === 'downloaded' && !validLoIds.has(String(loId)));

      if (!orphans.length) return;

      // Nome das aulas órfãs pro aviso — vem do grid ANTIGO (ainda cacheado, antes
      // do saveGrid sobrescrever), já que sumiram do grid fresco. Best-effort.
      const oldGrid = offlineStorage.getGrid(enrollId);
      const oldClasses = collectClassesFromGrid(oldGrid);
      const names = orphans
        .map(loId => { const lo = findLO(oldGrid, loId); return lo && (lo.title || lo.name); })
        .filter(Boolean);

      // classLearningObjectId (estável por aula-slot, sobrevive à troca de vídeo)
      // mapeia o órfão → mesma aula no grid fresco = a substituta. 0 = sem id estável
      // (C# manda 0 quando null, ex: download pré-deploy) → cai no fallback por turma.
      const notDownloaded = id => (enrollLos[String(id)] || 'idle') !== 'downloaded';
      const freshClasses = collectClassesFromGrid(grid);
      const freshByClassLoId = new Map();
      freshClasses.forEach(c => (c.learningObjects || []).forEach(lo => {
        if (lo.classLearningObjectId > 0) freshByClassLoId.set(String(lo.classLearningObjectId), lo);
      }));

      // Conta por tipo: troca de vídeo / caso ambíguo (updated) vs deleção confirmada
      // (id estável sem match no grid fresco → removed). kind 'mixed' se houver dos dois.
      const redownloadSet = new Set();
      let updatedCount = 0;
      let removedCount = 0;
      orphans.forEach(loId => {
        const oldLo = findLO(oldGrid, loId);
        const cloId = oldLo && oldLo.classLearningObjectId;
        if (cloId) {
          const match = freshByClassLoId.get(String(cloId));
          if (match) {
            updatedCount++;
            if (notDownloaded(match.id)) redownloadSet.add(match.id);
          } else {
            removedCount++;
          }
          return;
        }
        // Sem id estável (pré-deploy): ambíguo → conta como atualizado + fallback por turma.
        updatedCount++;
        const cls = oldClasses.find(c =>
          (c.learningObjects || []).some(lo => String(lo.id) === String(loId)));
        const classId = cls && String(cls.id);
        if (!classId) return;
        freshClasses.forEach(fc => {
          if (String(fc.id) !== classId) return;
          (fc.learningObjects || []).forEach(lo => { if (notDownloaded(lo.id)) redownloadSet.add(lo.id); });
        });
      });
      const redownloadLoIds = [...redownloadSet];
      const kind = updatedCount && removedCount ? 'mixed' : (removedCount ? 'removed' : 'updated');

      // Awaited antes do lease/touch do cacheEnrollStructure: se o órfão era o último
      // download, o clearLease conclui antes do touch recriar o lease.
      for (const loId of orphans) {
        await dispatch('removeLearningObject', { enrollId, id: loId });
      }

      commit('SET_PENDING_OUTDATED_NOTICE', {
        enrollId,
        courseName: (oldGrid && oldGrid.name) || null,
        count: orphans.length,
        kind,
        updatedCount,
        removedCount,
        names,
        redownloadLoIds
      });
    },

    // Remoção já ocorreu no reconcile; aqui só o redownload opcional. Payload vem do
    // modal (props), não do state — o slot pode ter sido sobrescrito por outro curso.
    async acknowledgeOutdatedNotice ({ state, dispatch, commit, rootState }, { enrollId, redownloadLoIds, redownload } = {}) {
      if (!enrollId) return;
      // Limpa o slot só se ainda for o aviso DESTE enroll (não apaga aviso de outro).
      if (state.pendingOutdatedNotice && String(state.pendingOutdatedNotice.enrollId) === String(enrollId)) {
        commit('CLEAR_PENDING_OUTDATED_NOTICE');
      }

      if (redownload && (redownloadLoIds || []).length) {
        // downloadLearningObject resolve o LO via rootState.enroll.current (creds
        // webcast / courseId / applicationId vivem no enroll). Se o usuário saiu
        // do curso, os loIds não resolvem e o download seria um no-op SILENCIOSO —
        // e como os órfãos já foram removidos, o reconcile não re-detecta → perda.
        // Só dispara quando o enroll atual bate; senão, avisa pra abrir o curso.
        const onThisEnroll = String(rootState.enroll.current && rootState.enroll.current.id) === String(enrollId);
        if (onThisEnroll) {
          for (const id of redownloadLoIds) {
            dispatch('downloadLearningObject', { enrollId, id });
          }
        } else {
          commit('toast/NEW', {
            type: 'info',
            message: 'Abra o curso para baixar as aulas atualizadas.'
          }, { root: true });
        }
      }
    },

    // Rehidrata state.positions a partir do progresso histórico do backend.
    // Resolve o caso "aula assistida antes (em outro device / sessão web /
    // pré-install) mas sem position local" — Meus Downloads mostrava ícone
    // ○ "não tocada" e resume offline recomeçava do zero.
    //
    // Fonte: enroll grid (ou full curso) tem `learningObject.progress` em %
    // (0-100). Backend não armazena segundos precisos (PUT /progress só envia
    // `complete: bool`), então estimamos: seconds = (progress/100) * duration.
    //
    // Política de overwrite:
    //   - Se positions local vazio   → popula com estimativa (updatedAt=0).
    //   - Se positions local marcado como estimativa (updatedAt=0) → atualiza
    //     com nova estimativa (caso backend tenha progredido em outro device).
    //   - Se positions local é REAL (updatedAt > 0) → NÃO sobrescreve.
    //     User assistiu offline; local é mais granular que % do backend.
    //
    // Idempotente. Best-effort: sem grid ou sem duration → no-op.
    syncPositionsFromGrid ({ state, commit }, { enrollId, grid }) {
      if (!enrollId || !grid) return;
      const enrollDownloads = state.downloads[enrollId];
      if (!enrollDownloads || !enrollDownloads.learningObjects) return;

      // Itera só sobre LOs baixados — não faz sentido rehidratar position de
      // aula que não está offline (não vai aparecer em Meus Downloads).
      Object.entries(enrollDownloads.learningObjects).forEach(([loId, status]) => {
        if (status !== 'downloaded') return;

        const lo = findLO(grid, loId);
        if (!lo) return;
        const progressPct = typeof lo.progress === 'number' ? lo.progress : null;
        if (progressPct == null || progressPct <= 0 || progressPct >= 100) return;

        // Duração pode vir de várias fontes (broadcast.duration > seconds > duration).
        // Sem duration confiável, não dá pra estimar — skip.
        const duration = (lo.broadcast && lo.broadcast.duration) || lo.seconds || lo.duration;
        if (!duration || duration <= 0) return;

        const existing = state.positions[enrollId]?.learningObjects?.[loId];
        const existingUpdatedAt = (existing && existing.updatedAt) || 0;

        // Position real do user (updatedAt > 0) é prioritária — não polui com
        // estimativa que pode estar atrás de um play offline recente.
        if (existing && existingUpdatedAt > 0) return;

        const estimatedSeconds = Math.floor((progressPct / 100) * duration);
        if (estimatedSeconds <= 0) return;

        // Margem 3s espelha o "concluído" check em outros lugares — se a
        // estimativa caísse muito perto do fim, marcaria a aula como já vista
        // mesmo com 1-2s faltando. Cap pra duration - 3 evita.
        const cappedSeconds = Math.min(estimatedSeconds, Math.floor(duration) - 3);
        if (cappedSeconds <= 0) return;

        commit('SET_POSITION', {
          enrollId, loId,
          seconds: cappedSeconds,
          updatedAt: 0  // marker "estimado/rehidratado"
        });
      });
    },

    // `auto: true` = retomada automática (resumePausedDownloads), não clique do
    // usuário. Só o clique zera o contador de retry — ver CLEAR_PARTIAL abaixo.
    async downloadLearningObject ({ commit, dispatch, state, rootState }, { enrollId, id, quality, auto = false }) {
      // Bloqueia downloads enquanto a migração de schema cripto roda — sem isso,
      // o rmdir('offline') da migração apagaria arquivos recém-baixados.
      if (state.migrating) {
        showMigrationInProgress(commit);
        return;
      }

      // Já na fila ou baixando: ignora o pedido duplicado, senão o mesmo LO
      // pode ser baixado por dois caminhos ao mesmo tempo (ícone da aula +
      // laço da turma) gravando no mesmo diretório.
      const currentLoStatus = state.downloads[enrollId]?.learningObjects?.[id] || 'idle';
      if (currentLoStatus === 'queued' || currentLoStatus === 'downloading') return;

      dispatch('cacheEnrollStructure', { enrollId });

      // Snapshot do LO ANTES de entrar na fila. Se o user navegar pra outro curso
      // enquanto o item espera, `rootState.enroll.current` muda; sem snapshot,
      // findLO retornaria null no callback → marcávamos 'downloaded' sem ter
      // baixado, e offline o `localVideoSrc` vinha vazio.
      const enroll = rootState.enroll.current;
      const learningObject = (rootState.enroll.currentLearningObjects || []).find(lo => String(lo.id) === String(id))
        || findLO(enroll, id);
      const webcast = (enroll && enroll.webcastIntegration) || {};
      const enrollName = enroll?.name || '';

      const hasVideo = !!(learningObject && learningObject.broadcast);
      const hasPdf = !!(learningObject && learningObject.pdf);
      const hasHtmlContent = !!(learningObject && learningObject.htmlContent);
      const hasAudio = !!(learningObject && learningObject.audio);
      if (!Capacitor.isNativePlatform() || !learningObject) {
        return;
      }
      // Defesa em profundidade — o ícone individual já é escondido pra esses
      // casos pelo _canDownloadLO no ComposedHeader, mas se algum caminho
      // programático (ou re-render race) disparar mesmo assim, abortamos aqui.
      const drm = rootState.learningObject && rootState.learningObject.drm;
      if (shouldSkipDownload(learningObject, drm, enroll && enroll.courseId)) {
        return;
      }
      // LO tipo texto (htmlContent) — conteúdo vive 100% no JSON do enroll
      // que já foi cacheado via cacheEnrollStructure logo acima. Marca como
      // downloaded direto, sem fetch nem notif. Consistente com o tratamento
      // no downloadClass (que já faz o mesmo pra LOs sem broadcast/pdf).
      //
      // Caveat conhecido: imagens externas embutidas no htmlContent (CDN)
      // continuam apontando pra URL HTTP — quebram offline. Resolução fora
      // de escopo desta feature (precisaria parser do HTML + imageCache).
      if (!hasVideo && !hasPdf && hasHtmlContent) {
        commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'downloaded' });
        commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 100 });
        commit('CLEAR_PARTIAL', { enrollId, loId: id });
        // Aula com progresso histórico (backend conhece) que acaba de ficar
        // 'downloaded' deve aparecer com a estimativa correta em Meus Downloads
        // imediatamente. Sem isso, esperaria pelo próximo cacheEnrollStructure.
        const gridForSync = offlineStorage.getGrid(enrollId);
        if (gridForSync) dispatch('syncPositionsFromGrid', { enrollId, grid: gridForSync });
        return;
      }
      // Demais tipos sem fetch (forms/quiz, externalContent): status fica idle
      // — UI não mente sobre disponibilidade offline (quiz precisa do backend
      // pra perguntas, externalContent puxa iframe externo).
      if (!hasVideo && !hasPdf && !hasAudio) {
        return;
      }

      const isPdf = !hasVideo && hasPdf;
      const isAudio = !hasVideo && !hasPdf && hasAudio;
      const courseId = enroll && enroll.courseId;

      // Vídeo HLS precisa de webcast.accessToken/applicationId pra falar com
      // Wowza/CoreMedia. PDF usa o auth token + applicationId via header — sem
      // webcast creds, mas precisa de courseId pro endpoint
      // /courses/{courseId}/learning-obj/{id}/pdf.
      if (hasVideo && !hasWebcastCredentials(webcast)) {
        showMissingCredentials(commit);
        return;
      }
      // PDF endpoint exige: courseId (path) + enroll.applicationId (header X-ApplicationId).
      // Sem qualquer um dos dois, o backend responde 401/404 e a fila falha
      // sem feedback útil — bloqueia cedo com o mesmo toast de sessão expirada
      // pra UX consistente com vídeo sem webcast credentials.
      if (isPdf && (!courseId || !(enroll && enroll.applicationId))) {
        showMissingCredentials(commit);
        return;
      }

      const loName = learningObject.name || learningObject.title || `Aula ${id}`;

      // Um pedido novo supera um cancelamento pendente. A marca só é varrida
      // quando a fila esvazia (downloadQueue.on('idle')) — sem esta limpeza,
      // cancelar e clicar em baixar de novo com outros itens ainda na fila não
      // fazia absolutamente nada, sem nem um erro. Vale também pra marca de
      // curso: baixar uma aula avulsa depois de remover o curso é intenção
      // nova, não sobra da remoção.
      _cancelledDownloads.delete(_cancelKey(enrollId, id));
      _cancelledDownloads.delete(_enrollCancelKey(enrollId));

      // Status otimista ANTES do preflight (rede, 2-6s) pra o ícone reagir no
      // clique. 'queued' (não 'downloading') porque com concurrency 1 o LO pode
      // ficar aguardando atrás de outra fila — vira 'downloading' só quando o
      // callback da fila roda. Reverte pra 'idle' se o preflight abortar.
      commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'queued' });
      commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });

      // Preflight: checa espaço/provider antes de enfileirar. Falha de rede é
      // silenciada — segue pro fluxo normal, que tenta de novo. PDF não tem
      // preflight de tamanho (só HLS, onde uma turma pode passar de 1GB).
      // Manifesto resolvido pelo preflight, repassado ao download() pra ele não
      // refazer os mesmos 3 round-trips encadeados (ver MANIFEST_REUSE_TTL_MS).
      let preflightManifest = null;
      if (hasVideo) {
        try {
          const pre = await hlsNativeDownloader.preflight(
            learningObject.broadcast.id,
            webcast.accessToken,
            webcast.applicationId,
            // MESMA qualidade que o download vai pedir. Sem isto o preflight
            // resolveria o variant default e o reuso baixaria a qualidade errada.
            { preferQuality: quality || 'low' }
          );
          preflightManifest = (pre && pre.info) || null;
          // Provider externo (YouTube/Vimeo): não baixa. Reverte e avisa.
          if (pre && pre.externalProvider) {
            commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
            showExternalProvider(commit);
            return;
          }
        } catch (err) {
          if (err instanceof InsufficientDiskSpaceError) {
            commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
            showInsufficientSpace(commit, err);
            return;
          }
          console.warn('[download] preflight falhou (segue mesmo assim):', err?.message || err);
        }
      }

      // Limpa o marcador de parcial — só aqui (pós-preflight) pra não apagá-lo
      // se o preflight tiver abortado. Se a retomada falhar, o catch reseta.
      //
      // SÓ no clique manual (`!auto`): limpar aqui zera o retryCount, e o
      // resume automático usa ESTA MESMA action. Antes, cada retomada automática
      // apagava o contador e o recriava em 1 — MAX_AUTO_RETRY era inalcançável e
      // um celular com rede oscilando retentava para sempre. O clique manual
      // continua sendo o gesto que descongela um parcial exausto.
      if (!auto) commit('CLEAR_PARTIAL', { enrollId, loId: id });

      // Dispara a notif ANTES de enfileirar. Sem isso, em fila com vários LOs
      // o user esperava (a) o turno da fila, (b) o plugin nativo subir o FGS
      // (Android) / agendar LocalNotifications (iOS) só pra ver a notif. iOS
      // mostra na hora; Android cacheia o texto no plugin pra a notif do FGS
      // (que sobe dentro do hlsNativeDownloader.download) já vir com o nome
      // correto da aula em vez do default "Preparando download...".
      downloadNotification.start(loName);
      // DEPOIS do start(): o refreshTotals é no-op enquanto a notif não está ativa.
      _notifAdd(1);

      _beginNativeSession();
      downloadQueue.add(async () => {
        // Removido enquanto esperava na fila? Aborta sem tocar no estado — quem
        // removeu já deixou tudo em 'idle' e apagou os arquivos. A notificação
        // foi iniciada antes do enqueue: sem o cancel ela ficaria pendurada em
        // "Preparando download..." se este era o último item da fila.
        if (_cancelledDownloads.has(_cancelKey(enrollId, id))) {
          // Devolve a vaga: esta aula não vai baixar, então não pode inflar o "de Y".
          _notifAdjust(-1);
          if (downloadQueue.size === 0) downloadNotification.cancel();
          return;
        }

        // Chegou a vez na fila: queued → downloading (transfere agora).
        _notifStart();
        commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'downloading' });
        // Snapshot do último progress reportado pra usar no marcador parcial
        // caso o download falhe por rede. Atualizado dentro do onProgress.
        let lastProgress = 0;
        try {
          const onProgress = (progress) => {
            lastProgress = progress;
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress });
            downloadNotification.update(enrollName, loName, _notifStarted, _notifTotal, progress);
          };

          // wifi-only quando o user não autorizou cellular (nem pref persistente
          // nem confirmação do ciclo). Lido no instante do download pra refletir
          // a confirmação que pode ter chegado depois do clique.
          const network = (!state.downloadOnCellular && !_cellularConfirmedThisCycle) ? 'wifi-only' : undefined;
          // Prioridade de reprodução: com aula em streaming aberta, o batch
          // entra com 1 conexão pra não disputar banda com o player. Lido no
          // instante do enqueue (como `network`) — capta playback que começou
          // depois do clique. O valor é snapshotado no batch nativo: playback
          // que começar DEPOIS só afeta a próxima aula da fila.
          const concurrency = (state.playbackPriority && rootState.dom?.streamingPlaybackActive)
            ? PLAYBACK_PRIORITY_CONCURRENCY
            : undefined;
          const localUrl = isAudio
            ? await audioNativeDownloader.download(id, learningObject.audio, onProgress, network)
            : isPdf
            ? await pdfNativeDownloader.download(id, courseId, enroll && enroll.applicationId, onProgress, network)
            : await hlsNativeDownloader.download({
              loId: id,
              broadcastId: learningObject.broadcast.id,
              token: webcast.accessToken,
              customerId: webcast.applicationId,
              onProgress,
              enrollId,
              quality,
              connectionType: rootState.dom?.connectionType,
              network,
              concurrency,
              manifestInfo: preflightManifest
            });
          if (localUrl === null) {
            // Defesa: hoje só PDF.download em ambiente não-nativo retorna null
            // (e o downloadLearningObject já abortou antes pra esse caso).
            // Antes esse branch marcava 'downloaded' pra provider externo
            // (YouTube/Vimeo) — bug que fazia a aula aparecer falsamente como
            // baixada. Hoje provider externo lança ExternalProviderError e
            // cai no catch abaixo; qualquer outro null aqui é estado
            // inesperado — marca idle pra não enganar o user.
            if (downloadQueue.size === 0) downloadNotification.cancel();
            commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
            return;
          }
          if (learningObject.broadcast) {
            // Dentro do `broadcast` porque a aba de IA só existe para aula de
            // vídeo — o caminho de turma já fazia assim, este buscava também
            // para PDF e áudio e gravava um ai.json que nunca seria exibido.
            if (aiPrefetchAllowed(enroll)) {
              dispatch('learningObject/getAI', { LearningObjectId: id, persistOnly: true }, { root: true });
            }
            dispatch('learningObject/prefetchTranscript', {
              loId: id,
              broadcastId: learningObject.broadcast.id,
              token: webcast.accessToken,
              customerId: webcast.applicationId
            }, { root: true });
            dispatch('prefetchSubtitles', {
              enrollId,
              loId: id,
              broadcastId: learningObject.broadcast.id,
              token: webcast.accessToken,
              customerId: webcast.applicationId
            });
          }
          commit('SET_PATH', { enrollId, type: 'learningObjects', id, url: localUrl });
          commit('SET_FORMAT', { enrollId, id, format: isAudio ? 'audio' : 'hls' });
          commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'downloaded' });
          // Turma que contém este LO pode ter acabado de ficar completa. Sem
          // isto, baixar aula por aula nunca acendia o ícone da turma.
          dispatch('syncClassAggregate', { enrollId, loId: id });
          // Rehidrata position estimada agora que o LO mudou pra 'downloaded'
          // — o sync que rodou no cacheEnrollStructure (antes do download)
          // filtrou esse LO porque status era 'idle'. Sem isso, aulas com
          // progresso histórico aparecem como "não tocadas" até outro
          // cacheEnrollStructure rodar (ex: user navegar no curso de novo).
          const gridForSync = offlineStorage.getGrid(enrollId);
          if (gridForSync) dispatch('syncPositionsFromGrid', { enrollId, grid: gridForSync });
          // Mede o tamanho real do diretório offline pra mostrar no painel.
          // Fire-and-forget: se falhar, UI degrada pra "sem badge de tamanho".
          computeOfflineSize(id).then(bytes => {
            if (bytes > 0) commit('SET_SIZE', { enrollId, loId: id, bytes });
          });
          // Última do batch: notifica com a contagem total da fila atual.
          if (downloadQueue.size === 0) downloadNotification.complete(_notifStarted);
        } catch (err) {
          // Disco pode ter encolhido entre o preflight e a hora real desta LO
          // na fila — o guard interno do download() relança InsufficientDiskSpaceError.
          // Cancelamento (removeLearningObject cancelou o batch): NÃO é falha.
          // Precisa sair antes de tudo — o ramo de "falha real" abaixo faria um
          // remove() redundante e, pior, commitaria SET_STATUS/CLEAR_PARTIAL por
          // cima do que o removeLearningObject acabou de gravar. Se o batch já
          // tivesse completado quando o cancel chegou, o fluxo seguia até
          // SET_STATUS('downloaded') DEPOIS de os arquivos terem sido apagados —
          // aula marcada como baixada, sem nada no disco.
          if (err instanceof CancelledError) {
            if (downloadQueue.size === 0) downloadNotification.cancel();
            return;
          }

          if (err instanceof InsufficientDiskSpaceError) {
            showInsufficientSpace(commit, err);
            _drainDownloadsOnDiskFull(commit, state);
            return;
          }
          // Provider externo (YouTube/Vimeo/MP4 progressivo) — preflight do
          // clique já deveria ter pego, mas se algum caminho silenciou (rede
          // intermitente no preflight cai no console.warn) cai aqui. Volta a
          // 'idle' e informa o user — NÃO marca downloaded.
          if (err instanceof ExternalProviderError) {
            commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
            commit('CLEAR_PARTIAL', { enrollId, loId: id });
            if (downloadQueue.size === 0) downloadNotification.cancel();
            showExternalProvider(commit);
            return;
          }
          // Falha de rede: NÃO apaga arquivos parciais. Plugin nativo já
          // persistiu os segmentos baixados em _batchdl_state — quando o user
          // disparar download de novo, o reconcileExistingFiles vai marcar
          // tudo que tá no disco como `done` e baixar só o que falta.
          const isNetErr = isNetworkError(err, rootState.dom?.isConnected);
          if (isNetErr) {
            console.warn('[download] pausado por rede:', err.message || err);
            // `quality`: retomar noutra qualidade mistura variants (ver SET_PARTIAL).
            commit('SET_PARTIAL', { enrollId, loId: id, progress: lastProgress, quality });
            commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
            // Toast varia conforme atingiu limite de retries ou não. SET_PARTIAL
            // já incrementou retryCount — leitura pós-commit dá o valor atual.
            const newRetryCount = state.partialDownloads[enrollId]?.[id]?.retryCount || 0;
            if (newRetryCount >= MAX_AUTO_RETRY) {
              showRetryExhausted(commit, loName);
            } else {
              showNetworkPaused(commit, loName);
            }
            if (downloadQueue.size === 0) downloadNotification.cancel();
            return;
          }
          // Falha real (HTTP 4xx/5xx não-transitório, manifest inválido, etc):
          // limpa arquivos parciais pra não deixar lixo no disco. ROTEIA POR
          // TIPO — chamar hls.remove num LO PDF apagaria o vídeo coexistente
          // (rmdir recursivo do diretório compartilhado offline/<loId>/), e
          // vice-versa. removeLearningObject (user-initiated) sim chama ambos.
          console.error(`[download] FALHA ao baixar ${isPdf ? 'PDF' : isAudio ? 'áudio' : 'vídeo'}:`, err.message || err);
          try {
            if (isPdf) {
              await pdfNativeDownloader.remove(id);
            } else if (isAudio) {
              await audioNativeDownloader.remove(id);
            } else {
              await hlsNativeDownloader.remove(id);
            }
          } catch (cleanupErr) {
            console.warn('[download] cleanup pós-falha falhou:', cleanupErr?.message || cleanupErr);
          }
          commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
          commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
          // Falha real apagou o arquivo → marcador parcial vira lixo. Sem limpar,
          // o resumePausedDownloads re-tentaria sobre um diretório já removido.
          commit('CLEAR_PARTIAL', { enrollId, loId: id });
          if (downloadQueue.size === 0) downloadNotification.cancel();
        }
      });
    },

    async downloadClass ({ commit, dispatch, state, rootState }, { enrollId, id, learningObjectIds, className, quality, fromEnrollRun = false }) {
      // Mesmas guardas do downloadLearningObject — ver comentários lá.
      if (state.migrating) {
        showMigrationInProgress(commit);
        return;
      }
      if ((state.downloads[enrollId]?.classes?.[id] || 'idle') === 'downloading') return;

      // Turma despachada pelo laço de "baixar curso" e o curso foi removido no
      // meio: não limpa nada e não baixa. O delete logo abaixo existe pra
      // "pedido novo supera cancelamento pendente", mas contra uma remoção de
      // CURSO ele agia ao contrário — cada turma que a fila desempilhava apagava
      // as marcas que a remoção tinha acabado de pôr, e o curso apagado voltava
      // a baixar. Clique DIRETO numa turma não passa por aqui: é intenção nova
      // do usuário e, como tal, supera a remoção anterior (limpa a marca).
      if (fromEnrollRun) {
        if (_cancelledDownloads.has(_enrollCancelKey(enrollId))) return;
      } else {
        _cancelledDownloads.delete(_enrollCancelKey(enrollId));
      }

      dispatch('cacheEnrollStructure', { enrollId });

      // Pedido novo supera cancelamento pendente — ver downloadLearningObject.
      // Limpa a turma E suas aulas: quem cancelou a turma marcou as duas coisas.
      _cancelledDownloads.delete(_classCancelKey(enrollId, id));
      (learningObjectIds || []).forEach(loId => _cancelledDownloads.delete(_cancelKey(enrollId, loId)));

      // Snapshot upfront — mesmo motivo do downloadLearningObject: o user pode
      // navegar pra outro curso enquanto a turma está enfileirada e antes do
      // callback rodar `rootState.enroll.current` aponta pra outro lugar.
      const enroll = rootState.enroll.current;
      const webcast = (enroll && enroll.webcastIntegration) || {};
      const loSnapshots = (learningObjectIds || []).map(loId => {
        const lo = (rootState.enroll.currentLearningObjects || []).find(l => String(l.id) === String(loId))
          || findLO(enroll, loId);
        return { loId, lo };
      });

      // Ledger da notificação para esta turma. `notifPlanned` = aulas que pretendemos
      // baixar (definido no bloco nativo); `notifReserved` = quanto já foi somado ao
      // total global. Separados porque os early-returns de erro acontecem ANTES do
      // _notifAdd, e devolver algo que não foi somado corromperia o "de Y".
      let notifPlanned = 0;
      let notifReserved = 0;

      // Preflight da turma INTEIRA: soma estimativas das aulas ainda não baixadas
      // que têm broadcast. Aborta cedo se o pacote não cabe no disco, antes de
      // marcar a turma como downloading ou enfileirar qualquer coisa.
      if (Capacitor.isNativePlatform()) {
        // Marca os LOs pendentes e baixáveis como 'queued' JÁ aqui (antes do
        // preflight de rede, que faz N fetches de manifesto) — os botões
        // per-aula travam (verde + cadeado) na hora, em vez de ficarem
        // clicáveis até a fila alcançar cada um. Skip-LOs (forms/external/
        // PDF-DRM) não entram (não têm botão). queued→downloading é feito no
        // loop abaixo; unqueueLos() reverte pra idle nos early-returns de erro.
        const drmForQueue = rootState.learningObject && rootState.learningObject.drm;
        const courseIdForQueue = enroll && enroll.courseId;
        const queuedLoIds = loSnapshots
          .filter(({ loId, lo }) =>
            (state.downloads[enrollId]?.learningObjects?.[loId] || 'idle') === 'idle' &&
            !shouldSkipDownload(lo, drmForQueue, courseIdForQueue)
          )
          .map(({ loId }) => loId);
        queuedLoIds.forEach(loId =>
          commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'queued' })
        );
        // Quantas aulas desta turma entram no "de Y" da notificação. O _notifAdd é
        // feito adiante (depois do start), mas o número vem daqui.
        notifPlanned = queuedLoIds.length;

        const unqueueLos = () => {
          // Early-return de erro: devolve ao ledger o que já tinha sido reservado
          // (0 se ainda não passamos pelo _notifAdd) junto com os status.
          _notifAdjust(-notifReserved);
          notifReserved = 0;
          notifPlanned = 0;
          queuedLoIds.forEach(loId => {
            if ((state.downloads[enrollId]?.learningObjects?.[loId] || 'idle') === 'queued') {
              commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'idle' });
            }
          });
        };

        // Mesma checagem que downloadLearningObject — sem credenciais Wowza,
        // preflight falha silenciosamente em cada item e turma "passa" no check.
        // A primeira aula ao baixar de verdade vai dar 401 e o user fica
        // perdido. Aborta agora se tiver alguma aula com broadcast pra baixar.
        const hasBroadcastToDownload = loSnapshots.some(s =>
          s.lo && s.lo.broadcast &&
          (state.downloads[enrollId]?.learningObjects?.[s.loId] || 'idle') !== 'downloaded'
        );
        if (hasBroadcastToDownload && !hasWebcastCredentials(webcast)) {
          unqueueLos();
          showMissingCredentials(commit);
          return;
        }

        // Análogo pro PDF — sem courseId/applicationId no enroll, todo download
        // de PDF da turma vai falhar com 401 sem mensagem útil. Aborta cedo se
        // existe pelo menos 1 LO PDF pendente e credenciais faltam.
        const hasPdfToDownload = loSnapshots.some(s =>
          s.lo && s.lo.pdf && !s.lo.broadcast &&
          (state.downloads[enrollId]?.learningObjects?.[s.loId] || 'idle') !== 'downloaded'
        );
        if (hasPdfToDownload && (!enroll || !enroll.courseId || !enroll.applicationId)) {
          unqueueLos();
          showMissingCredentials(commit);
          return;
        }

        const lessonsToPreflight = loSnapshots
          .filter(s =>
            s.lo && s.lo.broadcast &&
            (state.downloads[enrollId]?.learningObjects?.[s.loId] || 'idle') !== 'downloaded'
          )
          .map(s => ({
            broadcastId: s.lo.broadcast.id,
            token: webcast.accessToken,
            customerId: webcast.applicationId
          }));
        // Status otimista ANTES do preflightBatch (rede) pra o ícone reagir no
        // clique. Reverte pra 'idle' se o disco não couber.
        if (lessonsToPreflight.length) {
          commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'downloading' });
          commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 0 });
          try {
            // MESMA qualidade do download real — senão a estimativa de disco sai
            // no variant 'low' e a turma estoura o espaço no meio.
            await hlsNativeDownloader.preflightBatch(lessonsToPreflight, { preferQuality: quality || 'low' });
          } catch (err) {
            if (err instanceof InsufficientDiskSpaceError) {
              unqueueLos();
              commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'idle' });
              commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 0 });
              showInsufficientSpace(commit, err);
              return;
            }
            console.warn('[download] preflight da turma falhou (segue mesmo assim):', err?.message || err);
          }
        }
      }

      // Cobre os caminhos sem commit otimista (não-nativo / turma sem aula a
      // pré-validar). Idempotente quando já marcado acima.
      commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'downloading' });
      commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 0 });

      // Pre-computa pra disparar a notif ANTES de enfileirar (mesma justificativa
      // do downloadLearningObject). Skip se tudo já tá baixado — o queue.add abaixo
      // marca a turma como downloaded e retorna sem complete/cancel; sem o skip
      // a notif iniciada aqui ficaria pendurada.
      const clsName = className || `Turma ${id}`;
      const hasAnyPending = loSnapshots.some(s =>
        (state.downloads[enrollId]?.learningObjects?.[s.loId] || 'idle') !== 'downloaded'
      );
      if (hasAnyPending) downloadNotification.start(clsName);
      // DEPOIS do start(): refreshTotals é no-op com a notif inativa. É este add que
      // faz o "de Y" subir na hora quando a turma é enfileirada atrás de um download
      // já em curso — antes o downloadClass não alimentava contador nenhum.
      notifReserved = notifPlanned;
      _notifAdd(notifReserved);

      _beginNativeSession();
      downloadQueue.add(async () => {
        // Turma cancelada enquanto esperava a vez na fila: sai antes de tocar
        // em rede ou disco. Quem cancelou já deixou os status em 'idle'.
        if (_cancelledDownloads.has(_classCancelKey(enrollId, id))) {
          _notifAdjust(-notifReserved);
          notifReserved = 0;
          if (downloadQueue.size === 0) downloadNotification.cancel();
          return;
        }

        const totalLoCount = loSnapshots.length;
        if (totalLoCount === 0) {
          commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'downloaded' });
          commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 100 });
          return;
        }

        // Resolvido uma vez no escopo da fila — usado pelo skip no loop E pelo
        // cálculo de allOk no fim. Evita 2 acessos a rootState pela mesma chave.
        const drm = rootState.learningObject && rootState.learningObject.drm;
        const courseId = enroll && enroll.courseId;

        // Pula LOs que já foram baixadas — evita re-download desnecessário.
        // Para re-baixar uma específica corrompida, o usuário deve removê-la
        // primeiro (removeLearningObject) e depois disparar o download.
        const pendingSnapshots = loSnapshots.filter(s =>
          (state.downloads[enrollId]?.learningObjects?.[s.loId] || 'idle') !== 'downloaded'
        );
        const alreadyDoneAtStart = totalLoCount - pendingSnapshots.length;

        // Tudo já baixado: marca turma como concluída e sai.
        if (pendingSnapshots.length === 0) {
          commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'downloaded' });
          commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 100 });
          return;
        }

        const pendingTotal = pendingSnapshots.length;
        // Reconcilia o reservado no clique (queuedLoIds: só 'idle' e baixáveis) com o
        // que o lote vai de fato percorrer (pendingSnapshots: tudo que não está
        // 'downloaded'). A diferença são aulas skipáveis ou já em voo por outro
        // caminho; sem o ajuste o "de Y" ficaria menor que a posição exibida.
        _notifAdjust(pendingTotal - notifReserved);
        notifReserved = pendingTotal;
        let done = 0;
        // Trava de toast de provider externo dentro da turma: 1 toast por
        // chamada de downloadClass, mesmo que N LOs sejam YouTube/Vimeo. Sem
        // isso, baixar uma turma onde várias aulas são externas spammava o user.
        let externalProviderToastShown = false;

        // Progresso inicial da turma reflete o que já estava feito.
        const initialClassProgress = Math.round((alreadyDoneAtStart / totalLoCount) * 100);
        commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: initialClassProgress });

        let cancelledMidBatch = false;
        // Quantas aulas DESTA turma já começaram a transferir. Usado só pra devolver
        // ao ledger o que sobrou reservado se o lote for abortado no meio.
        let notifStartedHere = 0;
        for (const { loId, lo } of pendingSnapshots) {
          // `pendingSnapshots` foi congelado no início da task: se o usuário
          // removeu o curso/turma no meio, as aulas seguintes continuariam
          // baixando e reapareceriam. Checa a cada iteração e para o lote —
          // remover o curso é intenção de parar tudo, não só o item em voo.
          if (_cancelledDownloads.has(_classCancelKey(enrollId, id))) {
            cancelledMidBatch = true;
            break;
          }

          // Só esta aula foi cancelada (clique no ícone dela): pula e segue o
          // lote. O `allOk` no fim vê o buraco e deixa a turma como 'idle'.
          if (_cancelledDownloads.has(_cancelKey(enrollId, loId))) {
            _notifAdjust(-1);
            notifReserved--;
            continue;
          }

          // Skip LOs que NÃO devem ser baixados (forms/external puros, PDF
          // com DRM): não chama downloader, não marca downloaded, status fica
          // idle. Mas adianta o `done` pra o progresso visual da turma andar
          // sem trancar nesses LOs. Vide shouldSkipDownload pra detalhes.
          //
          // Testado ANTES de marcar 'downloading'. Quando esta checagem vinha
          // depois, o `continue` saía sem desfazer o status e a aula ficava
          // presa em 'downloading' para sempre: ícone girando, e
          // getEnrollDownloadSummary contando um ativo que não existe — o botão
          // do curso travava em "N de M" e passava a significar "cancelar"
          // mesmo com tudo baixado. Só o sweep do loadState, no boot seguinte,
          // desfazia. O `_notifStart` também não podia acontecer antes: o
          // `_notifAdjust(-1)` seguinte era anulado pelo clamp do ledger.
          if (shouldSkipDownload(lo, drm, courseId)) {
            // Não transfere nada: sai do "de Y" pra o total refletir só o que a
            // notificação de fato vai percorrer.
            _notifAdjust(-1);
            notifReserved--;
            done++;
            const classProgress = Math.round(((alreadyDoneAtStart + done) / totalLoCount) * 100);
            commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: classProgress });
            continue;
          }

          _notifStart();
          notifStartedHere++;
          commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'downloading' });
          commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress: 0 });

          const loDisplayName = (lo && (lo.name || lo.title)) || `Aula ${loId}`;


          // Declaradas FORA do try pra ficarem em escopo no catch — o cleanup
          // pós-falha real (HTTP 4xx, manifest inválido) roteia por tipo via
          // loHasPdf/loHasVideo. Antes ficavam dentro do try → ReferenceError
          // silencioso engolido pelo catch interno do cleanup, e os arquivos
          // parciais vazavam no disco.
          const loHasVideo = !!(lo && lo.broadcast);
          const loHasPdf = !!(lo && lo.pdf);
          const loHasAudio = !!(lo && lo.audio);

          try {
            if (Capacitor.isNativePlatform() && lo && (loHasVideo || loHasPdf || loHasAudio)) {
              const onProgress = (progress) => {
                commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress });
                // Ledger GLOBAL, não o `done`/`pendingTotal` da turma: com aulas
                // individuais e outra turma na mesma fila, o contador local mostrava
                // só o recorte desta turma e ignorava todo o resto.
                downloadNotification.update(clsName, loDisplayName, _notifStarted, _notifTotal, progress);
              };
              // wifi-only quando o user não autorizou cellular — vide
              // downloadLearningObject. Avaliado por LO pra captar confirmação
              // que tenha chegado no meio da turma.
              const network = (!state.downloadOnCellular && !_cellularConfirmedThisCycle) ? 'wifi-only' : undefined;
              // Prioridade de reprodução, avaliada por LO pelo mesmo motivo:
              // o aluno pode dar play no meio da turma e a próxima aula já
              // entra com 1 conexão. Vide downloadLearningObject.
              const concurrency = (state.playbackPriority && rootState.dom?.streamingPlaybackActive)
                ? PLAYBACK_PRIORITY_CONCURRENCY
                : undefined;
              const localUrl = loHasVideo
                ? await hlsNativeDownloader.download({
                  loId,
                  broadcastId: lo.broadcast.id,
                  token: webcast.accessToken,
                  customerId: webcast.applicationId,
                  onProgress,
                  enrollId,
                  quality,
                  connectionType: rootState.dom?.connectionType,
                  network,
                  concurrency
                })
                : loHasPdf
                  ? await pdfNativeDownloader.download(
                    loId,
                    enroll && enroll.courseId,
                    enroll && enroll.applicationId,
                    onProgress,
                    network
                  )
                  : await audioNativeDownloader.download(loId, lo.audio, onProgress, network);
              if (localUrl !== null) {
                if (loHasVideo) {
                  if (aiPrefetchAllowed(enroll)) {
                    dispatch('learningObject/getAI', { LearningObjectId: loId, persistOnly: true }, { root: true });
                  }
                  if (lo.broadcast) {
                    dispatch('learningObject/prefetchTranscript', {
                      loId,
                      broadcastId: lo.broadcast.id,
                      token: webcast.accessToken,
                      customerId: webcast.applicationId
                    }, { root: true });
                    dispatch('prefetchSubtitles', {
                      enrollId,
                      loId,
                      broadcastId: lo.broadcast.id,
                      token: webcast.accessToken,
                      customerId: webcast.applicationId
                    });
                  }
                  commit('SET_FORMAT', { enrollId, id: loId, format: 'hls' });
                }
                commit('SET_PATH', { enrollId, type: 'learningObjects', id: loId, url: localUrl });
              }
              commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'downloaded' });
              // Sucesso limpa o marcador parcial (paridade com downloadLearningObject).
              // Sem isso, um LO que falhou e depois completou via turma fica
              // 'downloaded' com partial fantasma; o resumePausedDownloads re-dispara
              // o download na próxima reconexão e, se falhar, joga o LO pra idle+laranja
              // sem o user ter feito nada.
              commit('CLEAR_PARTIAL', { enrollId, loId });
              // Mede tamanho real do diretório (vide downloadLearningObject).
              computeOfflineSize(loId).then(bytes => {
                if (bytes > 0) commit('SET_SIZE', { enrollId, loId, bytes });
              });
            } else if (lo && !loHasVideo && !loHasPdf) {
              // Aqui só chega htmlContent (texto) — forms/external puros foram
              // skipados acima via shouldSkipDownload. Marca downloaded direto
              // sem fetch: o conteúdo vive no JSON cacheado por cacheEnrollStructure.
              commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'downloaded' });
              commit('CLEAR_PARTIAL', { enrollId, loId });
            }
            // se `!lo` (snapshot vazio, ex: estrutura não cacheada): deixa idle.
          } catch (err) {
            // Cancelamento: o batch nativo desta aula foi abortado por uma
            // remoção. Não commita nada — quem cancelou já ajustou o estado —
            // mas o escopo decide o destino do lote: turma/curso removidos
            // param tudo (senão o conteúdo removido reaparecia sozinho); uma
            // aula cancelada sozinha só sai do lote.
            if (err instanceof CancelledError) {
              if (_cancelledDownloads.has(_classCancelKey(enrollId, id))) {
                cancelledMidBatch = true;
                break;
              }
              continue;
            }

            if (err instanceof InsufficientDiskSpaceError) {
              // Disco esgotou no meio da turma — toast único + drena fila
              // (outros downloads pendentes / outra turma na fila vão falhar
              // pelo mesmo motivo, sem drain seria um spam de toasts).
              showInsufficientSpace(commit, err);
              _drainDownloadsOnDiskFull(commit, state);
              return;
            }
            // Provider externo (YouTube/Vimeo/MP4 progressivo) dentro de uma
            // turma: pula esse LO mantendo idle e continua os demais. Toast
            // só na primeira ocorrência da turma — sem isso uma turma com 5
            // vídeos externos spammava 5 toasts iguais.
            if (err instanceof ExternalProviderError) {
              console.warn('[download] LO em provider externo, pulando:', loId);
              commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'idle' });
              commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress: 0 });
              commit('CLEAR_PARTIAL', { enrollId, loId });
              if (!externalProviderToastShown) {
                showExternalProvider(commit);
                externalProviderToastShown = true;
              }
              continue;
            }
            const isNetErr = isNetworkError(err, rootState.dom?.isConnected);
            if (isNetErr) {
              console.warn('[download] LO pausado por rede:', loId, err.message || err);
              // Guarda parcial pra UI mostrar "Continuar". progressAtPause aqui
              // é melhor approximado pelo último valor do batch (lo individual
              // dentro de downloadClass não tem lastProgress fácil). Usamos o
              // que está no store.
              const lastProgress = state.progress[enrollId]?.learningObjects?.[loId] || 0;
              // `quality`: retomar noutra qualidade mistura variants (ver SET_PARTIAL).
              commit('SET_PARTIAL', { enrollId, loId, progress: lastProgress, quality });
              commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'idle' });
              commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress: 0 });
              const newRetryCount = state.partialDownloads[enrollId]?.[loId]?.retryCount || 0;
              if (newRetryCount >= MAX_AUTO_RETRY) {
                showRetryExhausted(commit, loDisplayName);
              } else {
                showNetworkPaused(commit, loDisplayName);
              }
              // Continua processando próximas LOs da turma — a próxima também
              // vai falhar por rede, mas é o caminho mais consistente: cada LO
              // recebe seu próprio marcador parcial e seu toast.
              // Alternativa de produto: drain a turma toda como no disk-full.
              // Mantemos individual pra POC.
              done++;
              const classProgress = Math.round(((alreadyDoneAtStart + done) / totalLoCount) * 100);
              commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: classProgress });
              continue;
            }
            console.warn('[download] Falha ao baixar LO', loId, err);
            // Falha real: limpa arquivos parciais. ROTEIA POR TIPO — vide
            // comentário detalhado no catch do downloadLearningObject.
            try {
              if (loHasPdf && !loHasVideo) {
                await pdfNativeDownloader.remove(loId);
              } else if (loHasAudio && !loHasVideo) {
                await audioNativeDownloader.remove(loId);
              } else {
                await hlsNativeDownloader.remove(loId);
              }
            } catch (cleanupErr) {
              console.warn('[download] cleanup pós-falha LO falhou:', cleanupErr?.message || cleanupErr);
            }
            commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'idle' });
            commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress: 0 });
            // Falha real apagou o arquivo → marcador parcial vira lixo (vide downloadLearningObject).
            commit('CLEAR_PARTIAL', { enrollId, loId });
            // Falha real NÃO incrementa o `done` da turma. Antes caía pro
            // done++ abaixo e o progresso ia a 100% com aulas que claramente
            // não baixaram — `allOk` final marca turma como 'idle' mas a barra
            // chegava em 100% no caminho, transmitindo "completou e falhou"
            // em vez de "completou parcialmente". Skipping aqui deixa a barra
            // refletir só o que de fato foi baixado.
            continue;
          }

          done++;
          // Progresso da turma considera o total de LOs (já feitas + recém-feitas).
          const classProgress = Math.round(((alreadyDoneAtStart + done) / totalLoCount) * 100);
          commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: classProgress });
        }

        // Cancelado no meio: quem removeu já deixou LOs e turma em 'idle' e
        // apagou os arquivos. Seguir daqui só recommitaria estado por cima e
        // rodaria o sync de posições de um curso que não existe mais.
        if (cancelledMidBatch) {
          // Devolve as aulas que sobraram reservadas e nunca vão transferir, senão o
          // "de Y" de um download que continue na fila fica inflado pelo lote morto.
          _notifAdjust(-(notifReserved - notifStartedHere));
          notifReserved = notifStartedHere;
          if (downloadQueue.size === 0) downloadNotification.cancel();
          return;
        }

        // Considera turma OK quando TODOS os LOs baixáveis estão downloaded.
        // LOs skipáveis (forms/external puros, PDF-DRM) ficam idle pra sempre
        // — sem ignorá-los aqui, turma com qualquer um deles nunca chegaria
        // 100%, dando UX de "download incompleto" pra algo que nunca pode
        // completar.
        const allOk = learningObjectIds.every(loId => {
          const s = loSnapshots.find(snap => snap.loId === loId);
          if (s && shouldSkipDownload(s.lo, drm, courseId)) return true;
          return (state.downloads[enrollId]?.learningObjects?.[loId] || 'idle') === 'downloaded';
        });
        commit('SET_STATUS', { enrollId, type: 'classes', id, status: allOk ? 'downloaded' : 'idle' });

        // Rehidrata positions estimadas dos LOs que mudaram pra 'downloaded'
        // durante esta turma. Uma chamada cobre todos os LOs do enroll (o
        // sync é idempotente; LOs com play real são pulados pela política
        // de updatedAt). Roda só uma vez depois do loop pra evitar overhead.
        const gridForSync = offlineStorage.getGrid(enrollId);
        if (gridForSync) dispatch('syncPositionsFromGrid', { enrollId, grid: gridForSync });

        // Só finaliza a notificação se a fila esvaziou — mesmo guard das 5 saídas
        // do caminho individual. Sem ele, "baixar turma A" e depois "turma B"
        // fazia A finalizar a notificação (complete → _active = false) enquanto B
        // ainda baixava; como start() é no-op com _active true, B seguia sem
        // NENHUM feedback e o user via "concluído" com 30min de download pela frente.
        if (downloadQueue.size === 0) {
          if (allOk) {
            // `_notifStarted` (ledger do ciclo), não o `done` da turma: a fila pode
            // ter tido aulas individuais e outra turma, e "N aulas baixadas" deve
            // contar o ciclo inteiro — que é o que a notificação vinha exibindo.
            downloadNotification.complete(_notifStarted);
          } else {
            downloadNotification.cancel();
          }
        }
      });
    },

    // Recalcula o status agregado das turmas que contêm `loId` (ou de todas, se
    // omitido). O status de turma é ARMAZENADO, não derivado dos LOs — então
    // precisa ser reescrito toda vez que o conjunto de aulas baixadas muda.
    //
    // Faltava o sentido de SUBIDA: só o downloadClass marcava a turma como
    // 'downloaded'. Baixando aula por aula, todas as aulas ficavam baixadas e o
    // ícone da turma continuava em 'idle' pra sempre — só mudava se o usuário
    // clicasse no botão da própria turma.
    //
    // A regra de "completa" é a MESMA do downloadClass: LO não-baixável (form ou
    // link externo puro, PDF bloqueado por DRM) não conta. A revalidação de
    // descida não aplicava esse filtro, então uma turma com quiz, uma vez
    // rebaixada, nunca mais conseguia voltar pra 'downloaded'.
    syncClassAggregate ({ state, commit, rootState }, { enrollId, loId = null }) {
      const grid = offlineStorage.getGrid(enrollId);
      if (!grid) return;

      const drm = rootState.learningObject && rootState.learningObject.drm;
      // courseId só é conhecido quando o enroll em questão é o aberto. Sem ele o
      // shouldSkipDownload não casa a regra de DRM e trata o PDF como baixável —
      // o pior caso é a turma não fechar em 'downloaded', nunca o contrário.
      const current = rootState.enroll && rootState.enroll.current;
      const courseId = current && String(current.id) === String(enrollId) ? current.courseId : null;

      const statusOf = (id) =>
        state.downloads[enrollId]?.learningObjects?.[id] || 'idle';

      collectClassesFromGrid(grid).forEach(cls => {
        const los = cls.learningObjects || [];
        if (!los.length) return;
        if (loId != null && !los.some(lo => String(lo.id) === String(loId))) return;

        // Lote em andamento manda no próprio status — o downloadClass resolve no
        // fim. Mexer aqui faria a turma piscar 'idle' entre uma aula e outra.
        if ((state.downloads[enrollId]?.classes?.[cls.id] || 'idle') === 'downloading') return;

        const relevant = los.filter(lo => !shouldSkipDownload(lo, drm, courseId));
        if (!relevant.length) return;

        const done = relevant.filter(lo => statusOf(lo.id) === 'downloaded').length;
        commit('SET_STATUS', {
          enrollId, type: 'classes', id: cls.id,
          status: done === relevant.length ? 'downloaded' : 'idle'
        });
        commit('SET_PROGRESS', {
          enrollId, type: 'classes', id: cls.id,
          progress: Math.round((done / relevant.length) * 100)
        });
      });
    },

    async removeLearningObject ({ commit, dispatch, state }, { enrollId, id }) {
      // Marca ANTES de qualquer await: se este LO ainda estiver na fila (ou for
      // baixado pelo laço de uma turma), a execução aborta em vez de re-baixar o
      // que o usuário acabou de remover. Cobre também removeAllFromGroup e
      // removeAllFromEnroll, que passam por aqui.
      _cancelledDownloads.add(_cancelKey(enrollId, id));

      // Chama ambos em paralelo: cada downloader é idempotente quando o LO
      // não tem arquivo dele (PDF.remove sem document.pdf vira no-op, HLS.remove
      // sem manifest vira no-op). O rmdir recursivo do hls cuida de qualquer
      // resto que tenha sobrado no diretório compartilhado offline/<loId>/.
      await Promise.all([
        pdfNativeDownloader.remove(id).catch(e => console.warn('[download] pdf remove falhou:', e?.message || e)),
        audioNativeDownloader.remove(id).catch(e => console.warn('[download] audio remove falhou:', e?.message || e)),
        hlsNativeDownloader.remove(id)
      ]);
      commit('SET_STATUS', { enrollId, type: 'learningObjects', id, status: 'idle' });
      commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id, progress: 0 });
      commit('SET_PATH', { enrollId, type: 'learningObjects', id, url: null });
      commit('SET_FORMAT', { enrollId, id, format: null });
      commit('CLEAR_SIZE', { enrollId, loId: id });
      // Os .vtt somem no rmdir recursivo acima; sem limpar a entry, um
      // re-download futuro apontaria pra arquivos inexistentes até o
      // prefetch novo commitar.
      commit('SET_SUBTITLES', { enrollId, loId: id, list: null });
      // User explicitamente removeu → marcador parcial não faz mais sentido.
      commit('CLEAR_PARTIAL', { enrollId, loId: id });

      // Revalida as turmas que continham esse LO — mesma regra da subida, pra
      // os dois sentidos não divergirem no critério de "turma completa".
      dispatch('syncClassAggregate', { enrollId, loId: id });

      if (!hasAnyDownloads(state.downloads, enrollId)) {
        commit('SET_CACHED', { enrollId, value: false });
        offlineStorage.clearEnroll(enrollId);
        // Sem download no enroll → lease perde propósito. Apaga arquivo nativo
        // + state JS pra não deixar lixo em _batchdl_state/leases/. AWAITED: quando
        // o acknowledge do OutdatedLessonsNotice remove o último órfão e em seguida
        // re-baixa a aula nova (→ lease/touch), o clearLease precisa terminar ANTES
        // pra não apagar, em corrida, o lease recém-criado pela aula nova.
        await dispatch('lease/clearLease', { enrollId }, { root: true }).catch(() => {});
      }
    },

    // Remove todos os downloads de um enroll (curso inteiro). Itera SERIAL pra
    // não saturar o filesystem nativo com N rmdirs concorrentes — UX aceita
    // alguns segundos a mais em troca de menos contention. Reutiliza
    // removeLearningObject (que já cuida de cripto, paths, partial e do
    // cleanup final via hasAnyDownloads → clearEnroll quando o último LO sai).
    async removeAllFromGroup ({ dispatch }, { enrollId, loIds }) {
      for (const loId of (loIds || [])) {
        await dispatch('removeLearningObject', { enrollId, id: loId });
      }
    },

    // Curso inteiro. Reusa o downloadClass turma a turma em vez de enfileirar
    // todas as aulas de uma vez: o agregado por turma continua correto, o
    // cancelamento por turma segue funcionando, e o par com removeAllFromEnroll
    // fica simétrico.
    //
    // Serial de propósito — a PQueue tem concorrência 1 de qualquer forma, e
    // aguardar cada turma faz o cancelamento no meio do curso valer já na turma
    // seguinte, em vez de só depois de tudo enfileirado.
    async downloadEnroll ({ dispatch }, { enrollId, groups, quality }) {
      // Começar um curso limpa a marca de cancelamento do curso anterior.
      _cancelledDownloads.delete(_enrollCancelKey(enrollId));

      for (const group of (groups || [])) {
        const ids = (group && group.learningObjectIds) || [];
        if (!ids.length) continue;
        // Cancelou o curso inteiro no meio do laço: para aqui. Só a checagem
        // por turma não bastava — ela não alcança turma que ainda não commitou
        // status, que é a maioria enquanto o laço avança.
        if (_cancelledDownloads.has(_enrollCancelKey(enrollId))) break;
        if (_cancelledDownloads.has(_classCancelKey(enrollId, group.id))) continue;
        await dispatch('downloadClass', {
          enrollId,
          id: group.id,
          learningObjectIds: ids,
          className: group.className,
          quality,
          fromEnrollRun: true
        });
      }
    },

    async removeAllFromEnroll ({ commit, dispatch, state }, { enrollId }) {
      const enrollState = state.downloads[enrollId];
      if (!enrollState) return;

      // Aula pausada por falha de rede é gravada como 'idle' + entrada em
      // partialDownloads (o status volta pra idle justamente pra ela poder ser
      // retomada). Filtrar só por status deixava esses segmentos no disco: o
      // curso sumia da tela — hasAnyDownloads passava a ser falso e o
      // clearEnroll apagava grid e lease —, o usuário ficava sem como liberar o
      // espaço, e na reconexão seguinte o resumePausedDownloads achava o
      // parcial e re-baixava o curso que ele acabara de apagar.
      const partialsOfEnroll = Object.keys((state.partialDownloads || {})[enrollId] || {});
      const loIds = [...new Set([
        ...Object.keys(enrollState.learningObjects || {})
          .filter(id => enrollState.learningObjects[id] !== 'idle'),
        ...partialsOfEnroll
      ])];

      // Remover o curso é intenção de parar TUDO: marca antes dos awaits pra
      // qualquer lote em voo abortar já na próxima aula, em vez de pular aula
      // por aula até o fim da lista. A marca de CURSO é o que alcança as turmas
      // que o downloadEnroll ainda nem despachou.
      _cancelledDownloads.add(_enrollCancelKey(enrollId));
      Object.keys(enrollState.classes || {})
        .forEach(id => _cancelledDownloads.add(_classCancelKey(enrollId, id)));

      for (const loId of loIds) {
        await dispatch('removeLearningObject', { enrollId, id: loId });
      }

      // Zera flags de classes que tinham status agregado (download por turma).
      // Os LOs internos já foram limpos acima; aqui só sincroniza o agregado.
      const classIds = Object.keys(enrollState.classes || {})
        .filter(id => enrollState.classes[id] !== 'idle');
      classIds.forEach(id => {
        commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'idle' });
        commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 0 });
      });
    },

    async removeClass ({ commit, dispatch, state }, { enrollId, id, learningObjectIds }) {
      // Marca ANTES de qualquer await (mesma razão de removeLearningObject): a
      // chave da turma aborta o lote em voo; as chaves dos LOs abortam também os
      // downloads individuais dessas aulas que estejam esperando na fila.
      _cancelledDownloads.add(_classCancelKey(enrollId, id));
      (learningObjectIds || []).forEach(loId => _cancelledDownloads.add(_cancelKey(enrollId, loId)));

      commit('SET_STATUS', { enrollId, type: 'classes', id, status: 'idle' });
      commit('SET_PROGRESS', { enrollId, type: 'classes', id, progress: 0 });
      await Promise.all((learningObjectIds || []).map(async loId => {
        // Ambos em paralelo — vide comentário em removeLearningObject.
        await Promise.all([
          pdfNativeDownloader.remove(loId).catch(e => console.warn('[download] pdf remove falhou:', e?.message || e)),
          audioNativeDownloader.remove(loId).catch(e => console.warn('[download] audio remove falhou:', e?.message || e)),
          hlsNativeDownloader.remove(loId)
        ]);
        commit('SET_STATUS', { enrollId, type: 'learningObjects', id: loId, status: 'idle' });
        commit('SET_PROGRESS', { enrollId, type: 'learningObjects', id: loId, progress: 0 });
        commit('SET_PATH', { enrollId, type: 'learningObjects', id: loId, url: null });
        commit('SET_FORMAT', { enrollId, id: loId, format: null });
        commit('CLEAR_SIZE', { enrollId, loId });
        commit('CLEAR_PARTIAL', { enrollId, loId });
      }));
      if (!hasAnyDownloads(state.downloads, enrollId)) {
        commit('SET_CACHED', { enrollId, value: false });
        offlineStorage.clearEnroll(enrollId);
        dispatch('lease/clearLease', { enrollId }, { root: true }).catch(() => {});
      }
    },

    async applyDrmEnforcement ({ commit, state, rootState }, { courseId }) {
      if (!Capacitor.isNativePlatform()) return;
      // Idempotência: navegar entre cursos com flag ativa não pode trocar
      // o contexto do modal já aberto. Próxima visita re-detecta.
      if (state.pendingDrmPurge) return;

      const drm = rootState.learningObject && rootState.learningObject.drm;
      if (!drm || drm.codCurso != courseId) return;
      if (drm.desabilitarDownloadAulas !== true) return;

      const enroll = (rootState.enroll.enrolls || []).find(e => e.courseId == courseId);
      if (!enroll) return;

      const ds = state.downloads[enroll.id];
      if (!ds) return;
      const loCount = Object.values(ds.learningObjects || {}).filter(s => s !== 'idle').length;
      if (!loCount) return;

      commit('SET_PENDING_DRM_PURGE', {
        courseId,
        courseName: enroll.name || '',
        enrollIds: [enroll.id],
        loCount
      });
    },

    async executePendingDrmPurge ({ commit, dispatch, state }) {
      const pending = state.pendingDrmPurge;
      if (!pending) return;
      // Serial pra não saturar o filesystem nativo. Se falhar no meio,
      // CLEAR não roda e próximo getDRM completa o que restou.
      for (const enrollId of pending.enrollIds) {
        await dispatch('removeAllFromEnroll', { enrollId });
      }
      commit('CLEAR_PENDING_DRM_PURGE');
    }
  }
};
